import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import {
  TAG_CONDITION_GABLE,
  TAG_POSITION_BOTTOM,
  type BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { hardCodedAutoPlacedPropLabels } from "building-types/entities/labelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import type { GeneratedRoof, GeneratedRoofSoffit } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofRails } from "building-types/settings/roofTypes";
import { EaveCutType, EaveFriezeBoardMode, EaveType, PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { PropViewNoteType } from "building-types/settings/viewNotesSettings";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import * as constants from "common/constants/buildingObjects";
import {
  DEFAULT_GABLE_ROOF_VENT_WIDTH,
  FASCIA_THICKNESS,
  FASCIA_TOP_TRIM_SIZE,
  FASCIA_TOP_TRIM_THICKNESS,
  GABLE_ROOF_VENT_THICKNESS,
} from "common/constants/roofTrimConstants";
import type { Table } from "common/data/table";
import { assert } from "common/utils/assertOfType";
import { radianToDegree } from "common/utils/math";
import uid from "common/utils/uid";
import { getPropSrc } from "doc/elements/propCatalog/propCatalogHelpers";
import { isOpenEave } from "doc/fasciaTopTrimSizeToValue";
import { propNew } from "doc/propTools";
import { DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE, DEFAULT_EAVE_RETURN_DISTANCE } from "doc/settings/defaultSettings";
import { defaultPropViewNotes } from "doc/settings/defaultViewNoteSettings";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { baseDescriptorNew, faceArrayViewEntityNew, viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import { aabb3XLength } from "geom/aabb3";
import type { Extrusion, Face } from "geom/brep";
import {
  faceOrNullCopy,
  faceOrNullMove,
  faceByThreePoints,
  faceNew,
  faceOrNullMoveAlongNormal,
  faceReverse,
  tagFace,
} from "geom/brep";
import { tagFaces } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { clipFace, clipFaces, clipRail3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { BSP_EPSILON } from "geom/bspTypes";
import { COMPACTNESS_EPSILON, DIST_EPSILON } from "geom/core";
import {
  extrudeFace,
  extrudeFaceAlongNormal,
  extrudeFaceAlongPolyLine3,
  extrudePolyLine2AlongNormal,
  extrusionToBRep,
} from "geom/extrude";
import { line2Tangent } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeRotateAroundU,
  planeCopy,
  planeCopyMoved,
  planeEval,
  planeMove,
  planeMoveAlongNormal,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject,
  planeProjectPolyLine3,
  planeReverse,
} from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeNormal,
  polyLine2Length,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Copy,
  polyLine3CopyReversed,
  polyLine3EdgeCount,
  polyLine3EdgeEnd,
  polyLine3EdgeLength,
  polyLine3EdgeSlope,
  polyLine3EdgeSlopeInZ,
  polyLine3EdgeStart,
  polyLine3EdgeTangent,
  polyLine3EndPoint,
  polyLine3EvalEdge,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3IsClosed,
  polyLine3Length,
  polyLine3Move,
  polyLine3New,
  polyLine3OnPlane,
  polyLine3Reverse,
  polyLine3ReverseCopy,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { extrudeAlongThreeRails, extrudeAlongTwoRails, threeRailExtrusionToFaces } from "geom/threeRailExtrusion";
import { vec2AngleBetween, vec2Dist, vec2Normalize, vec2Rotate } from "geom/vec2";
import { vec3Add, vec3Cross, vec3Dist, vec3Normalize, vec3Sub } from "geom/vec3";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../elements/profileTools";
import {
  boxEaveProfile,
  getSoffitDepth,
  getSoffitDepthFromEaveType,
  hasEaveSquaredCut,
  hasValidFriezeBoards,
  plumbEaveReturnCrossSectionProfile,
  plumbRafterTailExtensionSoffitProfile,
  roofHasCorner,
  slopedEaveProfile,
  squaredEaveReturnCrossSectionProfile,
  squaredFasciaProfile,
  squaredMiniHipEndTrimSideProfile,
  squaredSlopedBoxEaveProfile,
  squaredSlopeEaveProfile,
} from "../elements/roofTrimUtility";
import { ventExtrusion } from "../elements/window/windowUtils";
import { clipFriezeRailsOutsideWalls, friezeBoardAlongRail } from "./friezeBoard";
import type { GableDetailsOptions } from "./GableDetailsOptions";
import { includeElementBasedOnOrientation } from "./multiGableRoofTools";
import { pushMissingProfileReferenceIssue } from "./profileMissingReference";
import { collectEaveReturnOverhangClipping } from "./roofs/roofOverhangClippingVolumes";
import { coplanarRailsNew } from "./roofs/roofRails";
import { makeSoffit } from "./roofs/roofSoffits";
import { reprojectFriezeBoardRail } from "./roofs/roofTrimRails";

const REMOVE_RAKE_EAVES_OVERLAP_EPSILON = 0.01;
const REMOVE_MINI_HIP_END_TRIM_OVERLAP = 0.01;
const FASCIA_BOARD_GENERATION_THRESHOLD = 0.05;
const FASCIA_BOARD_DUMMY_FASCIA_SIZE = 0.05;
const SOFFIT_FASCIA_BOARD_OVERLAP = 0.01;
const MAX_HEIGHT_DIFFERENCE_FOR_FULL_RETURN = 2;
const FRIEZE_BOARD_SAME_END_POINT_EPSILON = 0.01;

function isValidSlopeForEaveReturn(slope: number): boolean {
  const absSlope = Math.abs(slope);

  return absSlope !== Number.POSITIVE_INFINITY && absSlope > DIST_EPSILON;
}

export async function gableDetailsAlongRail({
  subRoof,
  subRoofs,
  walls,
  rail,
  roofPerimeterRail,
  edge,
  endRoofOverhang,
  exteriorWallOffsetWithFinish,
  startEaveOverhang,
  endEaveOverhang,
  addStartDetails,
  addEndDetails,
  propSettings,
  mergedPropLibrary,
  wallSettings,
  profileSource,
  fasciaBoards,
  fasciaTopTrims,
  friezeBoards,
  soffits,
  roofTopFaces,
  gableVentFaces,
  props,
  includeFriezeBoards,
  discardBSP,
  overlapBSP,
  insideBSP,
  wallOverlapForFriezeBoardBSPs,
  isEndSide,
  roofMaterialTrimAndProps = {},
  issues,
}: GableDetailsOptions): Promise<void> {
  if (!rail) {
    return;
  }

  const { roofSettings, roofVolume, gableVents, isDormer } = subRoof;
  const { roofTopBSP, neighborRoofs } = roofVolume;
  const { startNeighbor, endNeighbor } = neighborRoofs;

  const {
    eaveFasciaSize,
    eaveType,
    eaveCutType: settingsEaveCutType = EaveCutType.PLUMB,
    rakeBracketParams: rakeBracketParamsFromRoofSettings = [],
    rakeBracketParamsNormalized: rakeBracketParamsNormalizedFromRoofSettings = false,
    rakeBracketAtPeak: rakeBracketAtPeakFromRoofSettings = false,
    rakeBracketTruncationParams: rakeBracketTruncationParamsFromRoofSettings = [],
    eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
    eaveExtensionAtCoplanarRake = DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE,
    rakeBracketPropId: rakeBracketPropIdFromRoofSettings,
    addRafterTailExtension,
    eaveReturnRoofSlope,
    eaveReturnRoofMaterial,
    rafterTailExtensionEdgeOffset,
    rafterTailExtension,
    rafterTailDepth,
    rafterTailClosed,
    rakeFriezeBoard: rakeFriezeBoardFromRoofSettings,
    miniHipOrientation = StreetOrientationMode.FRONT_FACES,
    fullReturnOrientation = StreetOrientationMode.FRONT_FACES,
    includeEaveReturnFasciaTopTrim,
    eaveFriezeBoardExtents = EaveFriezeBoardMode.EAVE_AND_EAVE_RETURNS,
    eaveFriezeBoard,
    soffitMaterial,
    rakeFasciaMaterial,
    eaveFasciaMaterial,
    fasciaTopTrimMaterial: fasciaTopTrimMaterialFromRoofSettings,
    rakeOverhang,
    includeFasciaTopTrim: includeFasciaTopTrimFromRoofSettings,
    overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  } = roofSettings;

  const eaveCutType = roofPerimeterRail?.eaveCutType ?? settingsEaveCutType;
  const startEaveCutType = roofPerimeterRail?.nextPerimeterRail?.eaveCutType ?? settingsEaveCutType;
  const endEaveCutType = roofPerimeterRail?.prevPerimeterRail?.eaveCutType ?? settingsEaveCutType;

  const {
    includeFasciaTopTrim: includeFasciaTopTrimFromEdgeOverride,
    fasciaTopTrimSize: fasciaTopTrimSizeFromEdgeOverride = FASCIA_TOP_TRIM_SIZE,
    fasciaTopTrimMaterial: fasciaTopTrimMaterialFromEdgeOverride,
    friezeBoard: friezeBoardFromEdgeOverride,
    includeRakeBrackets: includeRakeBracketsFromEdgeOverride,
    rakeBracketParams: rakeBracketParamsFromEdgeOverride = [],
    rakeBracketParamsNormalized: rakeBracketParamsNormalizedFromEdgeOverride = false,
    rakeBracketAtPeak: rakeBracketAtPeakFromEdgeOverride = false,
    rakeBracketTruncationParams: rakeBracketTruncationParamsFromEdgeOverride = [],
    rakeBracketPropId: rakeBracketPropIdFromEdgeOverride,
  } = roofMaterialTrimAndProps;

  const includeFasciaTopTrim = includeFasciaTopTrimFromEdgeOverride ?? includeFasciaTopTrimFromRoofSettings;
  const fasciaTopTrimSize = fasciaTopTrimSizeFromEdgeOverride;
  const addFriezeBoards = hasValidFriezeBoards(friezeBoardFromEdgeOverride) ?? includeFriezeBoards;
  const rakeFriezeBoard = friezeBoardFromEdgeOverride ?? rakeFriezeBoardFromRoofSettings;
  const useRakeBracketParamsFromEdgeOverride = includeRakeBracketsFromEdgeOverride !== undefined;

  const rakeBracketParams = useRakeBracketParamsFromEdgeOverride
    ? rakeBracketParamsFromEdgeOverride
    : rakeBracketParamsFromRoofSettings;
  const rakeBracketParamsNormalized = useRakeBracketParamsFromEdgeOverride
    ? rakeBracketParamsNormalizedFromEdgeOverride
    : rakeBracketParamsNormalizedFromRoofSettings;
  const rakeBracketAtPeak = useRakeBracketParamsFromEdgeOverride
    ? rakeBracketAtPeakFromEdgeOverride
    : rakeBracketAtPeakFromRoofSettings;
  const rakeBracketTruncationParams = useRakeBracketParamsFromEdgeOverride
    ? rakeBracketTruncationParamsFromEdgeOverride
    : rakeBracketTruncationParamsFromRoofSettings;
  const rakeBracketPropId = useRakeBracketParamsFromEdgeOverride
    ? rakeBracketPropIdFromEdgeOverride
    : rakeBracketPropIdFromRoofSettings;

  const fasciaTopTrimMaterial = fasciaTopTrimMaterialFromEdgeOverride ?? fasciaTopTrimMaterialFromRoofSettings;
  const hasSquaredCut = hasEaveSquaredCut(eaveType, eaveCutType);
  const hasStartSquaredCut = hasEaveSquaredCut(eaveType, startEaveCutType);
  const hasEndSquaredCut = hasEaveSquaredCut(eaveType, endEaveCutType);

  const railPlane = planeByGableRail(rail);
  const invRailPlane = planeReverse(planeCopy(railPlane));

  const railElements = rail.length;

  const fasciaThickness = Math.min(rakeOverhang - COMPACTNESS_EPSILON, FASCIA_THICKNESS);

  const soffitDepth = getSoffitDepthFromEaveType(eaveType, roofSettings);

  const startSlope = polyLine3EdgeSlopeInZ(rail, 0);
  const endSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

  if (!isValidSlopeForEaveReturn(startSlope)) {
    addStartDetails = false;
  }

  if (!isValidSlopeForEaveReturn(endSlope)) {
    addEndDetails = false;
  }

  const startSlopeAngle = Math.atan(startSlope);
  const endSlopeAngle = Math.atan(endSlope);

  const railStartContractDist = startSlopeAngle > DIST_EPSILON ? startEaveOverhang / Math.cos(startSlopeAngle) : 0.0;
  const railEndContractDist = endSlopeAngle > DIST_EPSILON ? endEaveOverhang / Math.cos(endSlopeAngle) : 0.0;

  const contractedRail = polyLine3ExtendBothEnds(polyLine3Copy(rail), -railStartContractDist, -railEndContractDist);

  const wallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallSettings,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );

  const soffitWidth = endRoofOverhang - exteriorWallOffsetWithSheathing;

  const { eaveReturnOverhang = endRoofOverhang - wallOffsetFromOverhangDatum } = roofSettings;

  const eaveReturnOverhangFromSheathing =
    eaveReturnOverhang + wallOffsetFromOverhangDatum - exteriorWallOffsetWithSheathing;

  const returnFasciaBoards: FaceArrayViewEntity[] = [];
  const returnSoffits: GeneratedRoofSoffit[] = [];
  const returnFriezeBoards: FaceArrayViewEntity[] = [];
  const returnTopFaces: FaceArrayViewEntity[] = [];

  const hasStartCorner = roofHasCorner({
    subRoof,
    isEndSide,
    isProfileStartSide: true,
    overlapBSP,
  });

  const hasEndCorner = roofHasCorner({
    subRoof,
    isEndSide,
    isProfileStartSide: false,
    overlapBSP,
  });

  const hasFriezeReturnAtStart = roofHasCorner({
    subRoof,
    isEndSide,
    isProfileStartSide: true,
    overlapBSP,
    offset: 0.5,
  });

  const hasFriezeReturnAtEnd = roofHasCorner({
    subRoof,
    isEndSide,
    isProfileStartSide: false,
    overlapBSP,
    offset: 0.5,
  });

  const startEaveWidth = hasStartCorner
    ? startEaveOverhang + eaveReturnDistance - wallOffsetFromOverhangDatum
    : startEaveOverhang + eaveExtensionAtCoplanarRake - wallOffsetFromOverhangDatum;
  const endEaveWidth = hasEndCorner
    ? endEaveOverhang + eaveReturnDistance - wallOffsetFromOverhangDatum
    : endEaveOverhang + eaveExtensionAtCoplanarRake - wallOffsetFromOverhangDatum;

  const [startX, startY, startZ] = polyLine3StartPoint(rail);
  const [endX, endY, endZ] = polyLine3EndPoint(rail);
  const minZ = Math.min(startZ, endZ);
  const span = vec2Dist(startX, startY, endX, endY);
  const [nX, nY, nZ] = planeNormal(railPlane);

  // get the peak point
  let peakEdgeIndex = -1;
  let maxPeakHeight = Number.NEGATIVE_INFINITY;
  for (let i = 0, l = polyLine3EdgeCount(contractedRail) - 1; i < l; i++) {
    const [, , prevZ] = polyLine3EdgeStart(contractedRail, i);
    const [, , currZ] = polyLine3EdgeEnd(contractedRail, i);
    const [, , nextZ] = polyLine3EdgeEnd(contractedRail, i + 1);

    if (currZ <= prevZ + DIST_EPSILON || currZ <= nextZ + DIST_EPSILON || currZ <= maxPeakHeight) {
      continue;
    }

    maxPeakHeight = currZ;
    peakEdgeIndex = i;
  }

  function clipExtrusion(extrusion: Extrusion): Face[] {
    const { faces } = extrusionToBRep(extrusion);
    const clippingNodes = [clippingNodeNew(discardBSP, true)];

    return clipFaces({
      faces,
      options: {
        clippingNodes,
        epsilon: MULTIGABLE_BSP_EPSILON,
        coplanarsToFront: false,
      },
    });
  }

  function addGableVentIfNeeded() {
    const includeTriangleGableVent = isEndSide
      ? gableVents.includeEndTriangleGableVent
      : gableVents.includeStartTriangleGableVent;

    if (!includeTriangleGableVent || peakEdgeIndex < 0) {
      return;
    }

    const triangleGableVentBaseWidth =
      (isEndSide ? gableVents.endTriangleGableVentBaseWidth : gableVents.startTriangleGableVentBaseWidth) ??
      DEFAULT_GABLE_ROOF_VENT_WIDTH;

    if (triangleGableVentBaseWidth < DIST_EPSILON) {
      return;
    }

    const [prevX, prevY, prevZ] = polyLine3EdgeStart(contractedRail, peakEdgeIndex);
    const [currX, currY, currZ] = polyLine3EdgeEnd(contractedRail, peakEdgeIndex);
    const [nextX, nextY, nextZ] = polyLine3EdgeEnd(contractedRail, peakEdgeIndex + 1);

    const prevSlope = (currZ - prevZ) / vec2Dist(prevX, prevY, currX, currY);
    const nextSlope = (currZ - nextZ) / vec2Dist(nextX, nextY, currX, currY);

    const height = (triangleGableVentBaseWidth * prevSlope * nextSlope) / (prevSlope + nextSlope);

    const prevBase = height / prevSlope;
    const nextBase = height / nextSlope;

    const gableVentPlane = planeMoveAlongNormal(
      planeReverse(planeCopy(railPlane)),
      -endRoofOverhang + GABLE_ROOF_VENT_THICKNESS,
    );

    const [peakX, peakY] = planeProject(gableVentPlane, currX, currY, currZ);
    const ventProfile = polyLine2New([
      peakX - prevBase,
      peakY - height,
      peakX,
      peakY,
      peakX + nextBase,
      peakY - height,
      peakX - prevBase,
      peakY - height,
    ]);

    const [prevNormalX, prevNormalY] = polyLine2EdgeNormal(ventProfile, 0, true);
    const [nextNormalX, nextNormalY] = polyLine2EdgeNormal(ventProfile, 1, true);
    const [midNormalX, midNormalY] = vec2Normalize(prevNormalX + nextNormalX, prevNormalY + nextNormalY);

    let offsetX = 0;
    let offsetY = -soffitDepth;

    if (addFriezeBoards) {
      let friezeBoardOffset = 0;
      const roofHandle = { type: "ROOF" as const, id: subRoof.id };
      for (const { friezeBoard, verticalOffset = 0 } of rakeFriezeBoard || []) {
        const descriptor = getActiveProfileDescriptor(profileSource, friezeBoard, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
        if (!descriptor) {
          pushMissingProfileReferenceIssue(issues, profileSource, friezeBoard, roofHandle, "rakeFriezeBoard");
          continue;
        }
        const profile = getProfileExtrusionDescriptorProfile(descriptor);
        const friezeBoardProfileAABB = polyLine2AABB2(profile);
        friezeBoardOffset = Math.max(friezeBoardOffset, aabb2Height(friezeBoardProfileAABB) + verticalOffset);
      }
      offsetX -= midNormalX * friezeBoardOffset;
      offsetY -= midNormalY * friezeBoardOffset;
    }

    polyLine2Move(ventProfile, offsetX, offsetY);

    const gableVentExtrusions: Extrusion[] = [];

    gableVentExtrusions.push(
      ...ventExtrusion({
        outline: offsetPolyLine2(ventProfile, GABLE_ROOF_VENT_THICKNESS * 0.5),
        plane: gableVentPlane,
        divNum: Math.floor(height / 2),
        angle: -20,
        clipAgainstOutline: true,
        thickness: 1.5,
      }),
    );

    const ventFrameLoops = [ventProfile, polyLine2Reverse(offsetPolyLine2(ventProfile, GABLE_ROOF_VENT_THICKNESS))];
    const ventFrameFace = faceNew(ventFrameLoops, gableVentPlane);

    gableVentExtrusions.push(extrudeFaceAlongNormal(ventFrameFace, GABLE_ROOF_VENT_THICKNESS));

    gableVentFaces.push(
      ...gableVentExtrusions.map((extrusion) => faceArrayViewEntityNew(extrusionToBRep(extrusion).faces)),
    );
  }

  async function addRakeBracketsIfNeeded(): Promise<void> {
    const propId = rakeBracketPropId;
    if (!propId) {
      return;
    }

    const propSrc = await getPropSrc(propSettings, propId, mergedPropLibrary);

    if (!propSrc) {
      return;
    }

    const projectedRail = planeProjectPolyLine3(planeNewXY(), rail);
    const [wallNormX, wallNormY] = polyLine2EdgeNormal(projectedRail, 0);

    if (!includeRakeBracketsFromEdgeOverride) {
      return;
    }

    // rakeBracketPropAttributes.viewNotes is undefined until someone configures a note on this catalog prop, so merge with the default table rather than indexing it directly.
    const rakeBracketPropAttributes = propSettings[propId];
    const rakeBracketViewNotes = mergeViewNoteTables(defaultPropViewNotes, rakeBracketPropAttributes?.viewNotes);
    const rakeBracketDescriptor = () =>
      viewEntityDescriptorNew(uid(), ViewEntityType.PROP, rakeBracketViewNotes[PropViewNoteType.PROP]);

    const radians = vec2AngleBetween(0, 1, -wallNormX, -wallNormY) + Math.PI;

    let xoffset = (aabb3XLength(propSrc.boundingBox) * GLTF_ASSET_SCALE) / 2;
    let yoffset = -endRoofOverhang + exteriorWallOffsetWithFinish;

    const railCount = polyLine3EdgeCount(contractedRail);
    const startRailLength = polyLine3EdgeLength(contractedRail, 0);
    const endRailLength = polyLine3EdgeLength(contractedRail, railCount - 1);
    const railLength = Math.min(startRailLength, endRailLength);

    const bracketWidth = (aabb3XLength(propSrc.boundingBox) * GLTF_ASSET_SCALE) / 2;
    const bracketZOffset = (rail: PolyLine3, edgeIndex: number) => {
      const edgeSlope = Math.abs(polyLine3EdgeSlope(rail, edgeIndex));
      return edgeSlope * bracketWidth + Math.sqrt(soffitDepth * soffitDepth * (1 + edgeSlope * edgeSlope));
    };

    [xoffset, yoffset] = vec2Rotate(xoffset, yoffset, radians);

    if (rakeBracketParams) {
      if (rakeBracketParams.length > 0) {
        for (const param of rakeBracketParams) {
          const BRACKET_INSET_DISTANCE = rakeBracketParamsNormalized || param < 1 ? param * railLength : param;
          if (BRACKET_INSET_DISTANCE > railLength) {
            continue;
          }

          const [ax, ay, az] = polyLine3EvalEdge(contractedRail, 0, BRACKET_INSET_DISTANCE, false);
          props.push(
            propNew({
              room: null,
              src: propId,
              x: ax - xoffset,
              y: ay - yoffset,
              z: az - bracketZOffset(contractedRail, 0),
              rotationDegrees: radianToDegree(radians),
              catalogType: PropCatalogType.PROP_CATALOG,
              isExterior: true,
              labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
              descriptor: rakeBracketDescriptor(),
            }),
          );

          const [bx, by, bz] = polyLine3EvalEdge(
            contractedRail,
            railCount - 1,
            endRailLength - BRACKET_INSET_DISTANCE,
            false,
          );
          props.push(
            propNew({
              room: null,
              src: propId,
              x: bx - xoffset,
              y: by - yoffset,
              z: bz - bracketZOffset(contractedRail, railCount - 1),
              rotationDegrees: radianToDegree(radians),
              catalogType: PropCatalogType.PROP_CATALOG,
              isExterior: true,
              labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
              descriptor: rakeBracketDescriptor(),
            }),
          );
        }
      }
    }
    if (rakeBracketAtPeak && peakEdgeIndex >= 0) {
      const [ax, ay, az] = polyLine3EdgeEnd(contractedRail, peakEdgeIndex);
      props.push(
        propNew({
          room: null,
          src: propId,
          x: ax - xoffset,
          y: ay - yoffset,
          z: az - bracketZOffset(contractedRail, peakEdgeIndex),
          rotationDegrees: radianToDegree(radians),
          catalogType: PropCatalogType.PROP_CATALOG,
          isExterior: true,
          labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
          descriptor: rakeBracketDescriptor(),
        }),
      );
    }

    if (rakeBracketTruncationParams && railCount === 3) {
      const railLength = polyLine3EdgeLength(contractedRail, 1);
      if (rakeBracketTruncationParams.length > 0) {
        const zoffset = bracketZOffset(contractedRail, 1);
        for (const param of rakeBracketTruncationParams) {
          const BRACKET_INSET_DISTANCE = rakeBracketParamsNormalized || param < 1 ? param * railLength : param;

          const [ax, ay, az] = polyLine3EvalEdge(contractedRail, 1, BRACKET_INSET_DISTANCE, false);
          props.push(
            propNew({
              room: null,
              src: propId,
              x: ax - xoffset,
              y: ay - yoffset,
              z: az - zoffset,
              rotationDegrees: radianToDegree(radians),
              catalogType: PropCatalogType.PROP_CATALOG,
              isExterior: true,
              labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
              descriptor: rakeBracketDescriptor(),
            }),
          );
        }
      }
    }
  }

  function addEaveReturnFriezeBoard(
    faceArrays: FaceArrayViewEntity[],
    distance: number,
    isProfileStartSide: boolean,
    friezeZOffset: number = eaveFasciaSize,
  ) {
    const isBoxedFullReturnAndLegacyEave =
      eaveType === EaveType.BOXED_FULL_RETURN && eaveFriezeBoardExtents === EaveFriezeBoardMode.LEGACY_EAVE;
    const isNotBoxedFullReturnAndLegacyEave =
      eaveType !== EaveType.BOXED_FULL_RETURN && eaveFriezeBoardExtents === EaveFriezeBoardMode.LEGACY_EAVE;
    //return also if eaveReturnDistance is < 0.0
    if (
      !includeFriezeBoards ||
      (eaveFriezeBoardExtents !== EaveFriezeBoardMode.EAVE_AND_EAVE_RETURNS && !isBoxedFullReturnAndLegacyEave) ||
      distance < 0.0 ||
      isNotBoxedFullReturnAndLegacyEave
    ) {
      return;
    }

    let sweepProfile = eaveFriezeBoard;

    const parentRoofId = isEndSide ? startNeighbor.roofId : endNeighbor.roofId;
    const parentRoof = subRoofs[parentRoofId];

    if (parentRoof && subRoof.polygonId < 0) {
      const { roofSettings: intersectedSettings } = parentRoof;
      const { eaveFriezeBoard: parentEaveFriezeBoard } = intersectedSettings;

      if (hasValidFriezeBoards(parentEaveFriezeBoard)) {
        sweepProfile = parentEaveFriezeBoard;
      }
    }

    let profileWidth = 0;
    if (sweepProfile?.length === 1 && !isDormer) {
      //note: we are picking the first frieze board to get the profile width, this will not work if we have multiple frieze boards//
      //the real solution would be to move this eave return implementation to the new room trim implementation and generate the frieze boards there.
      const { friezeBoard } = sweepProfile[0];
      const descriptor = getActiveProfileDescriptor(profileSource, friezeBoard, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
      if (!descriptor) {
        pushMissingProfileReferenceIssue(
          issues,
          profileSource,
          friezeBoard,
          { type: "ROOF", id: subRoof.id },
          "eaveFriezeBoard",
        );
      } else {
        const profile = getProfileExtrusionDescriptorProfile(descriptor);
        const friezeBoardProfileAABB = polyLine2AABB2(profile);
        profileWidth = Math.max(profileWidth, aabb2Width(friezeBoardProfileAABB));
      }
    }

    if (!hasValidFriezeBoards(sweepProfile)) {
      return;
    }

    const eaveOverhang = isProfileStartSide
      ? startEaveOverhang - wallOffsetFromOverhangDatum
      : endEaveOverhang - wallOffsetFromOverhangDatum;
    const sign = isProfileStartSide ? 1 : -1;
    let [x0, y0, z0] = isProfileStartSide ? polyLine3StartPoint(rail) : polyLine3EndPoint(rail);
    const { uX, uY, uZ } = railPlane;

    const extendToSideThickness = !isDormer;

    if (extendToSideThickness) {
      distance += eaveOverhang;
      z0 -= friezeZOffset;
    } else {
      x0 += sign * uX * eaveOverhang;
      y0 += sign * uY * eaveOverhang;
      z0 += sign * uZ * eaveOverhang - friezeZOffset + 0.1;
    }

    const x1 = x0 + sign * uX * distance;
    const y1 = y0 + sign * uY * distance;
    const z1 = z0 + sign * uZ * distance;

    const friezeExensionRail = isProfileStartSide
      ? polyLine3ByTwoPoints(x1, y1, z1, x0, y0, z0)
      : polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);

    const offsetAlongPlaneNormal = endRoofOverhang - exteriorWallOffsetWithSheathing;
    polyLine3Move(
      friezeExensionRail,
      nX * offsetAlongPlaneNormal,
      nY * offsetAlongPlaneNormal,
      nZ * offsetAlongPlaneNormal,
    );

    const friezeExtensionPlane = planeMoveAlongNormal(planeCopy(invRailPlane), -offsetAlongPlaneNormal);
    let coplanarRails = [coplanarRailsNew(friezeExtensionPlane, -nX, -nY, -nZ, [friezeExensionRail])];

    if (extendToSideThickness) {
      coplanarRails = reprojectFriezeBoardRail({
        coplanarRails,
        subRoofs,
        walls,
        offsetZ: -friezeZOffset,
      });
    }

    const [checkPtX, checkPtY, checkPtZ] = isProfileStartSide
      ? polyLine3StartPoint(friezeExensionRail)
      : polyLine3EndPoint(friezeExensionRail);

    for (const { rails } of coplanarRails) {
      for (const rail of rails) {
        const railExtended = polyLine3Copy(rail);

        if (profileWidth !== 0.0) {
          const [railCheckPtX, railCheckPtY, railCheckPtZ] = isProfileStartSide
            ? polyLine3StartPoint(rail)
            : polyLine3EndPoint(rail);
          const distance = vec3Dist(railCheckPtX, railCheckPtY, railCheckPtZ, checkPtX, checkPtY, checkPtZ);
          if (distance < FRIEZE_BOARD_SAME_END_POINT_EPSILON) {
            if (isProfileStartSide) {
              polyLine3ExtendEnd(railExtended, profileWidth);
            } else {
              polyLine3ExtendStart(railExtended, profileWidth);
            }
          }
        }

        friezeBoardAlongRail({
          rail: railExtended,
          slope: 0,
          overhang: 0,
          profileSource,
          roofSettings,
          friezePlaneNormal: [nX, nY, nZ],
          faceArrays,
          eaveType,
          sweepProfile,
          issues,
          handle: { type: "ROOF", id: subRoof.id },
        });
      }
    }
  }

  function addFriezeBoardExtensionAtSide(
    faceArrays: FaceArrayViewEntity[],
    baseLength: number,
    isProfileStartSide: boolean,
  ) {
    const isBoxedFullReturnAndLegacyEave =
      eaveType === EaveType.BOXED_FULL_RETURN && eaveFriezeBoardExtents === EaveFriezeBoardMode.LEGACY_EAVE;
    const isNotBoxedFullReturnAndLegacyEave =
      eaveType !== EaveType.BOXED_FULL_RETURN && eaveFriezeBoardExtents === EaveFriezeBoardMode.LEGACY_EAVE;
    // for avoiding regressions we are only checking eaveFriezeBoardExtents for the new trim generation
    if (
      isOpenEave[eaveType] ||
      (eaveFriezeBoardExtents !== EaveFriezeBoardMode.EAVE_AND_EAVE_RETURNS && !isBoxedFullReturnAndLegacyEave) ||
      isNotBoxedFullReturnAndLegacyEave
    ) {
      return;
    }

    const addDetails = isProfileStartSide ? addStartDetails : addEndDetails;
    const hasFriezeReturn = isProfileStartSide ? hasFriezeReturnAtStart : hasFriezeReturnAtEnd;
    let sweepProfile = eaveFriezeBoard;

    if (!addDetails || hasFriezeReturn) {
      return;
    }

    const parentRoofId = isEndSide ? startNeighbor.roofId : endNeighbor.roofId;

    const parentRoof = subRoofs[parentRoofId] ?? subRoof;

    const { roofSettings: intersectedSettings } = parentRoof;
    const {
      friezeBoardOrientation = StreetOrientationMode.FRONT_AND_SIDE_FACES,
      eaveFriezeBoard: parentEaveFriezeBoard,
    } = intersectedSettings;

    if (hasValidFriezeBoards(parentEaveFriezeBoard)) {
      sweepProfile = parentEaveFriezeBoard;
    }

    if (
      !hasValidFriezeBoards(sweepProfile) ||
      !includeElementBasedOnOrientation(invRailPlane, friezeBoardOrientation)
    ) {
      return;
    }

    let friezeExtension = baseLength;

    if (friezeExtension < DIST_EPSILON) {
      return;
    }

    if (!isProfileStartSide) {
      friezeExtension *= -1;
    }

    const offsetZ = (isProfileStartSide ? startZ : endZ) - minZ - eaveFasciaSize + 0.1;
    const [x, y, z] = isProfileStartSide ? polyLine3StartPoint(rail) : polyLine3EndPoint(rail);
    let { uX, uY, uZ } = railPlane;

    uX *= friezeExtension;
    uY *= friezeExtension;
    uZ *= friezeExtension;

    const [x0, y0, z0] = vec3Add(x, y, z, 0, 0, offsetZ);
    const [x1, y1, z1] = vec3Add(x0, y0, z0, uX, uY, uZ);

    const friezeExensionRail = isProfileStartSide
      ? polyLine3ByTwoPoints(x1, y1, z1, x0, y0, z0)
      : polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);

    const offsetAlongPlaneNormal = endRoofOverhang - exteriorWallOffsetWithSheathing;
    polyLine3Move(
      friezeExensionRail,
      nX * offsetAlongPlaneNormal,
      nY * offsetAlongPlaneNormal,
      nZ * offsetAlongPlaneNormal,
    );

    const friezeExtensionPlane = planeMoveAlongNormal(planeCopy(invRailPlane), -offsetAlongPlaneNormal);
    const coplanarRails = reprojectFriezeBoardRail({
      coplanarRails: [coplanarRailsNew(friezeExtensionPlane, -nX, -nY, -nZ, [friezeExensionRail])],
      subRoofs,
      walls,
      offsetZ: -eaveFasciaSize,
    });

    for (const { rails } of coplanarRails) {
      for (const reprojectedRail of rails) {
        const clippedFriezeRails = clipFriezeRailsOutsideWalls({
          wallOverlapForFriezeBoardBSPs,
          friezeRails: [reprojectedRail],
        });

        for (const clippedFriezeRail of clippedFriezeRails) {
          friezeBoardAlongRail({
            rail: clippedFriezeRail,
            slope: 0,
            overhang: 0,
            profileSource,
            roofSettings,
            friezePlaneNormal: [nX, nY, nZ],
            faceArrays,
            eaveType,
            sweepProfile,
            issues,
            handle: { type: "ROOF", id: subRoof.id },
          });
        }
      }
    }
  }

  function addBoxFullReturnFasciaTrimOptions(trimPlane: Plane) {
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;

    if (includeFasciaTopTrim && dist < -FASCIA_BOARD_GENERATION_THRESHOLD) {
      const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(trimPlane);

      const [xRailOffset, yRailOffset, zRailOffset] = [
        dist * nxFasciaBoardPlaneNorm,
        dist * nyFasciaBoardPlaneNorm,
        dist * nzFasciaBoardPlaneNorm,
      ];

      const extension = hasSquaredCut
        ? [
            nxFasciaBoardPlaneNorm * fasciaTopTrimSize,
            nyFasciaBoardPlaneNorm * fasciaTopTrimSize,
            nzFasciaBoardPlaneNorm * fasciaTopTrimSize,
          ]
        : [0, 0, 0];
      const frontOnlyRail = polyLine3New([
        rail[0],
        rail[1],
        rail[2],
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
      ]);
      const frontOnlyRailOffsetted = polyLine3Move(frontOnlyRail, xRailOffset, yRailOffset, zRailOffset);

      const extensionRailStart = polyLine3New([
        rail[0] + extension[0],
        rail[1] + extension[1],
        rail[2] + extension[2],
        frontOnlyRailOffsetted[0] + extension[0], //point in the plane of the front fascia board
        frontOnlyRailOffsetted[1] + extension[1], //point in the plane of the front fascia board
        frontOnlyRailOffsetted[2] + extension[2], //point in the plane of the front fascia board
      ]);
      addFasciaTrim(
        polyLine3Reverse(
          extensionRailStart,
        ) /*doing reverse copy here because the trim needs to be outside of the startRail*/,
        FASCIA_TOP_TRIM_THICKNESS,
        fasciaTopTrimSize,
        fasciaTopTrims,
        hasSquaredCut,
        startSlope,
        fasciaTopTrimMaterial,
      );
      const extensionRailEnd = polyLine3New([
        rail[railElements - 3] + extension[0],
        rail[railElements - 2] + extension[1],
        rail[railElements - 1] + extension[2],
        frontOnlyRailOffsetted[3] + extension[0], //point in the plane of the front fascia board
        frontOnlyRailOffsetted[4] + extension[1], //point in the plane of the front fascia board
        frontOnlyRailOffsetted[5] + extension[2], //point in the plane of the front fascia board
      ]);
      addFasciaTrim(
        extensionRailEnd,
        FASCIA_TOP_TRIM_THICKNESS,
        fasciaTopTrimSize,
        fasciaTopTrims,
        hasSquaredCut,
        endSlope,
        fasciaTopTrimMaterial,
      );
    }
  }

  function addBoxedFullReturnSoffit() {
    const soffitReturnPlane = planeMoveAlongNormal(
      planeCopy(railPlane),
      soffitWidth - eaveReturnOverhangFromSheathing + fasciaThickness,
    );
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(railPlane);
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;
    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    const baseRailOriginal = polyLine3New([
      rail[0],
      rail[1],
      rail[2],
      rail[railElements - 3],
      rail[railElements - 2],
      rail[railElements - 1],
    ]);

    const baseRail = polyLine3Move(baseRailOriginal, xRailOffset, yRailOffset, zRailOffset);
    const baseRailShortened = polyLine3Copy(baseRail);
    polyLine3ExtendBothEnds(baseRailShortened, -fasciaThickness);
    const endEaveReturnSlopeResolved = isEndSide ? eaveReturnRoofSlope ?? endSlope : eaveReturnRoofSlope ?? startSlope;

    const [nX, nY] = hasSquaredCut ? vec2Normalize(-endEaveReturnSlopeResolved, 1) : [0, 1];
    const eaveFasciaSizeAtEaveReturn = isEndSide ? eaveFasciaSize : eaveFasciaSize; //TODO: use correct eaveFasciaSize at start
    const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start
    const soffitOffsetX = -nX * getSoffitDepth(roofSettings) + nY * eaveFasciaThicknessAtEaveReturn;
    const soffitDepth = -nY * getSoffitDepth(roofSettings) - nX * eaveFasciaThicknessAtEaveReturn;

    const fullReturnPlaneOffSetted = hasSquaredCut
      ? planeMoveAlongNormal(planeCopy(railPlane), soffitWidth - eaveReturnOverhangFromSheathing + soffitOffsetX)
      : soffitReturnPlane;

    const soffitExtrusion = thickenExtrudeRailInZ(
      baseRailShortened,
      fullReturnPlaneOffSetted,
      soffitDepth,
      eaveReturnOverhangFromSheathing + nX * eaveFasciaSizeAtEaveReturn,
    );

    const { faces } = extrusionToBRep(soffitExtrusion);

    addGeneratedSoffit(returnSoffits, eaveType, faces, soffitExtrusion, soffitMaterial);
  }

  function addMiniHipShedFasciaBoardExtensions() {
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;

    //return if soffit is inside the eave return
    if (dist >= -FASCIA_BOARD_GENERATION_THRESHOLD) {
      return;
    }

    const { uX, uY, uZ } = railPlane;
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(railPlane);

    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    const baseRailOriginal = polyLine3New([
      rail[0],
      rail[1],
      rail[2],
      rail[railElements - 3],
      rail[railElements - 2],
      rail[railElements - 1],
    ]);
    const baseRail = polyLine3Move(baseRailOriginal, xRailOffset, yRailOffset, zRailOffset);

    if (addStartDetails) {
      const shiftForGelingFasciaBoards = hasStartSquaredCut ? -eaveFasciaSize : 0;
      const eavefasciaProfileStart = hasStartSquaredCut
        ? squaredEaveReturnCrossSectionProfile(eaveFasciaSize, startSlope, fasciaThickness, 0, 0, 0, true)
        : slopedEaveProfile(eaveFasciaSize, 0, fasciaThickness);

      const [startRailEndPtX, startRailEndPtY, startRailEndPtZ] = vec3Sub(
        baseRail[0],
        baseRail[1],
        baseRail[2],
        xRailOffset + shiftForGelingFasciaBoards * nxFasciaBoardPlaneNorm,
        yRailOffset + shiftForGelingFasciaBoards * nyFasciaBoardPlaneNorm,
        zRailOffset + shiftForGelingFasciaBoards * nzFasciaBoardPlaneNorm,
      );

      //note: the following tiny rail is added inorder for eave fascia board to gel with return eave fascia board
      const startRail = hasStartSquaredCut
        ? polyLine3New([
            startRailEndPtX,
            startRailEndPtY,
            startRailEndPtZ,
            baseRail[0],
            baseRail[1],
            baseRail[2],
            baseRail[0] + uX * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
            baseRail[1] + uY * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
            baseRail[2] + uZ * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
          ])
        : polyLine3New([startRailEndPtX, startRailEndPtY, startRailEndPtZ, baseRail[0], baseRail[1], baseRail[2]]);

      const fasciaRailsStart = clipRail3({
        rail: startRail,
        options: {
          clippingNodes: [clippingNodeNew(discardBSP)],
          coplanarsToFront: true,
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
      });

      for (const fasciaRail of fasciaRailsStart) {
        polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
        returnFasciaBoards.push(
          faceArrayViewEntityNew(
            extrudeFaceAlongPolyLine3([eavefasciaProfileStart], fasciaRail, 0, 0, 1).faces,
            baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
            eaveFasciaMaterial,
          ),
        );
      }
    }

    if (addEndDetails) {
      const shiftForGelingFasciaBoards = hasEndSquaredCut ? -eaveFasciaSize : 0;
      const eavefasciaProfileEnd = hasEndSquaredCut
        ? squaredEaveReturnCrossSectionProfile(eaveFasciaSize, endSlope, fasciaThickness, 0, 0, 0, true)
        : slopedEaveProfile(eaveFasciaSize, 0, fasciaThickness);

      const [endRailEndPtX, endRailEndPtY, endRailEndPtZ] = vec3Sub(
        baseRail[3],
        baseRail[4],
        baseRail[5],
        xRailOffset + shiftForGelingFasciaBoards * nxFasciaBoardPlaneNorm,
        yRailOffset + shiftForGelingFasciaBoards * nyFasciaBoardPlaneNorm,
        zRailOffset + shiftForGelingFasciaBoards * nzFasciaBoardPlaneNorm,
      );

      //note: the following tiny rail is added inorder for eave fascia board to gel with return eave fascia board
      const endRail = hasEndSquaredCut
        ? polyLine3New([
            endRailEndPtX,
            endRailEndPtY,
            endRailEndPtZ,
            baseRail[3],
            baseRail[4],
            baseRail[5],
            baseRail[3] - uX * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
            baseRail[4] - uY * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
            baseRail[5] - uZ * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
          ])
        : polyLine3New([endRailEndPtX, endRailEndPtY, endRailEndPtZ, baseRail[3], baseRail[4], baseRail[5]]);

      const fasciaRailsEnd = clipRail3({
        rail: polyLine3Reverse(endRail),
        options: {
          clippingNodes: [clippingNodeNew(discardBSP)],
          coplanarsToFront: true,
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
      });

      for (const fasciaRail of fasciaRailsEnd) {
        polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
        returnFasciaBoards.push(
          faceArrayViewEntityNew(
            extrudeFaceAlongPolyLine3([eavefasciaProfileEnd], fasciaRail, 0, 0, 1).faces,
            baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
            eaveFasciaMaterial,
          ),
        );
      }
    }
  }

  function addBoxFullReturnFasciaBoardExtensions() {
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;

    //return if soffit is inside the eave return
    if (dist >= -FASCIA_BOARD_GENERATION_THRESHOLD) {
      return;
    }

    const { uX, uY, uZ } = railPlane;
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(railPlane);

    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    const baseRailOriginal = polyLine3New([
      rail[0],
      rail[1],
      rail[2],
      rail[railElements - 3],
      rail[railElements - 2],
      rail[railElements - 1],
    ]);
    const baseRail = polyLine3Move(baseRailOriginal, xRailOffset, yRailOffset, zRailOffset);
    const shiftForGelingFasciaBoards = hasSquaredCut ? -eaveFasciaSize : 0;

    const roofSlope = isEndSide ? endSlope : startSlope;
    const eavefasciaProfileStart = hasSquaredCut
      ? squaredEaveReturnCrossSectionProfile(eaveFasciaSize, roofSlope, fasciaThickness, 0, 0, 0, true)
      : slopedEaveProfile(eaveFasciaSize, 0, fasciaThickness);

    const [startRailEndPtX, startRailEndPtY, startRailEndPtZ] = vec3Sub(
      baseRail[0],
      baseRail[1],
      baseRail[2],
      xRailOffset + shiftForGelingFasciaBoards * nxFasciaBoardPlaneNorm,
      yRailOffset + shiftForGelingFasciaBoards * nyFasciaBoardPlaneNorm,
      zRailOffset + shiftForGelingFasciaBoards * nzFasciaBoardPlaneNorm,
    );

    //note: the following tiny rail is added inorder for eave fascia board to gel with return eave fascia board
    const startRail = hasSquaredCut
      ? polyLine3New([
          startRailEndPtX,
          startRailEndPtY,
          startRailEndPtZ,
          baseRail[0],
          baseRail[1],
          baseRail[2],
          baseRail[0] + uX * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
          baseRail[1] + uY * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
          baseRail[2] + uZ * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
        ])
      : polyLine3New([startRailEndPtX, startRailEndPtY, startRailEndPtZ, baseRail[0], baseRail[1], baseRail[2]]);

    const fasciaRailsStart = clipRail3({
      rail: startRail,
      options: {
        clippingNodes: [clippingNodeNew(discardBSP)],
        coplanarsToFront: true,
        epsilon: MULTIGABLE_BSP_EPSILON,
      },
    });

    for (const fasciaRail of fasciaRailsStart) {
      polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
      returnFasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([eavefasciaProfileStart], fasciaRail, 0, 0, 1).faces,
          baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
          eaveFasciaMaterial,
        ),
      );
    }

    const eavefasciaProfileEnd = hasSquaredCut
      ? squaredEaveReturnCrossSectionProfile(eaveFasciaSize, roofSlope, fasciaThickness, 0, 0, 0, true)
      : slopedEaveProfile(eaveFasciaSize, 0, fasciaThickness);

    const [endRailEndPtX, endRailEndPtY, endRailEndPtZ] = vec3Sub(
      baseRail[3],
      baseRail[4],
      baseRail[5],
      xRailOffset + shiftForGelingFasciaBoards * nxFasciaBoardPlaneNorm,
      yRailOffset + shiftForGelingFasciaBoards * nyFasciaBoardPlaneNorm,
      zRailOffset + shiftForGelingFasciaBoards * nzFasciaBoardPlaneNorm,
    );

    //note: the following tiny rail is added inorder for eave fascia board to gel with return eave fascia board
    const endRail = hasSquaredCut
      ? polyLine3New([
          endRailEndPtX,
          endRailEndPtY,
          endRailEndPtZ,
          baseRail[3],
          baseRail[4],
          baseRail[5],
          baseRail[3] - uX * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
          baseRail[4] - uY * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
          baseRail[5] - uZ * FASCIA_BOARD_DUMMY_FASCIA_SIZE,
        ])
      : polyLine3New([endRailEndPtX, endRailEndPtY, endRailEndPtZ, baseRail[3], baseRail[4], baseRail[5]]);

    const fasciaRailsEnd = clipRail3({
      rail: polyLine3Reverse(endRail),
      options: {
        clippingNodes: [clippingNodeNew(discardBSP)],
        coplanarsToFront: true,
        epsilon: MULTIGABLE_BSP_EPSILON,
      },
    });

    for (const fasciaRail of fasciaRailsEnd) {
      polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
      returnFasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([eavefasciaProfileEnd], fasciaRail, 0, 0, 1).faces,
          baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
          eaveFasciaMaterial,
        ),
      );
    }
  }

  function addFasciaAndRafterTailExtension() {
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(railPlane);
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;
    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    const baseRailOriginal = polyLine3New([
      rail[0],
      rail[1],
      rail[2],
      rail[railElements - 3],
      rail[railElements - 2],
      rail[railElements - 1],
    ]);

    const baseRail = polyLine3Move(baseRailOriginal, xRailOffset, yRailOffset, zRailOffset);
    const eaveFasciaSizeAtEaveReturn = isEndSide ? eaveFasciaSize : eaveFasciaSize; //TODO: use correct eaveFasciaSize at start
    const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start
    const endEaveReturnSlopeResolved = isEndSide ? eaveReturnRoofSlope ?? endSlope : eaveReturnRoofSlope ?? startSlope;
    const length = polyLine3Length(baseRail);
    const fasciaProfile = hasSquaredCut
      ? squaredEaveReturnCrossSectionProfile(
          eaveFasciaSizeAtEaveReturn,
          endEaveReturnSlopeResolved,
          eaveFasciaThicknessAtEaveReturn,
          0,
          0,
          0,
          true,
        )
      : slopedEaveProfile(eaveFasciaSizeAtEaveReturn, 0, eaveFasciaThicknessAtEaveReturn);
    const extensionEdgeOffset = rafterTailExtensionEdgeOffset ?? DEFAULT_RAFTER_TAIL_EDGE_OFFSET;

    if (addRafterTailExtension && extensionEdgeOffset < length - DIST_EPSILON) {
      if (extensionEdgeOffset > DIST_EPSILON) {
        const baseRailShortenedLeft = polyLine3Copy(baseRail);
        polyLine3ExtendEnd(baseRailShortenedLeft, -(length - extensionEdgeOffset));

        const fasciaRailsLeft = clipRail3({
          rail: baseRailShortenedLeft,
          options: {
            clippingNodes: [clippingNodeNew(discardBSP)],
            coplanarsToFront: true,
            epsilon: MULTIGABLE_BSP_EPSILON,
          },
        });

        for (const fasciaRail of fasciaRailsLeft) {
          returnFasciaBoards.push(
            faceArrayViewEntityNew(
              extrudeFaceAlongPolyLine3([fasciaProfile], fasciaRail, 0, 0, 1).faces,
              baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
              rakeFasciaMaterial,
            ),
          );
        }

        const baseRailShortenedRight = polyLine3Copy(baseRail);
        polyLine3ExtendStart(baseRailShortenedRight, -(length - extensionEdgeOffset));

        const fasciaRailsRight = clipRail3({
          rail: baseRailShortenedRight,
          options: {
            clippingNodes: [clippingNodeNew(discardBSP)],
            coplanarsToFront: true,
            epsilon: MULTIGABLE_BSP_EPSILON,
          },
        });

        for (const fasciaRail of fasciaRailsRight) {
          returnFasciaBoards.push(
            faceArrayViewEntityNew(
              extrudeFaceAlongPolyLine3([fasciaProfile], fasciaRail, 0, 0, 1).faces,
              baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
              rakeFasciaMaterial,
            ),
          );
        }
      }
    } else {
      const fasciaRails = clipRail3({
        rail: baseRail,
        options: {
          clippingNodes: [clippingNodeNew(insideBSP)],
          coplanarsToFront: true,
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
      });

      for (const fasciaRail of fasciaRails) {
        polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
        returnFasciaBoards.push(
          faceArrayViewEntityNew(
            extrudeFaceAlongPolyLine3([fasciaProfile], fasciaRail, 0, 0, 1).faces,
            baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
            rakeFasciaMaterial,
          ),
        );
      }
    }
  }

  //this is rectangular volume object
  function addBoxMiniSoffit(miniPlane: Plane, fasciaBoardPlane: Plane, isShed: boolean = true) {
    const [xdiff, ydiff, zdiff] = vec3Sub(rail[railElements - 3], rail[railElements - 2], 0, rail[0], rail[1], 0);
    const [xNorm, yNorm, zNorm] = vec3Normalize(xdiff, ydiff, zdiff);
    const eaveFasciaSizeAtEaveReturn = isEndSide ? eaveFasciaSize : eaveFasciaSize; //TODO: use correct eaveFasciaSize at start
    const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(fasciaBoardPlane);
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;
    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    if (addStartDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[0],
        rail[1],
        rail[2],
        xNorm * startEaveWidth,
        yNorm * startEaveWidth,
        zNorm * startEaveWidth,
      );

      const startRailOriginal = polyLine3New([rail[0], rail[1], rail[2], railEndX, railEndY, railEndZ]);
      const startRail = polyLine3Move(startRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? startSlope;
      const [nX, nY] = hasStartSquaredCut ? vec2Normalize(-eaveReturnRoofSlopeResolved, 1) : [0, 1];
      const soffitOffsetX = -nX * getSoffitDepth(roofSettings) + nY * eaveFasciaThicknessAtEaveReturn;
      const soffitDepth = -nY * getSoffitDepth(roofSettings) - nX * eaveFasciaThicknessAtEaveReturn;

      const miniShedPlaneOffsetted = hasStartSquaredCut
        ? planeMoveAlongNormal(planeCopy(railPlane), soffitWidth - eaveReturnOverhangFromSheathing + soffitOffsetX)
        : miniPlane;

      const startRailShortened = polyLine3Copy(startRail);
      polyLine3ExtendStart(startRailShortened, nX * eaveFasciaSize - nY * fasciaThickness);
      isShed
        ? polyLine3ExtendEnd(startRailShortened, -nY * fasciaThickness)
        : polyLine3ExtendEnd(startRailShortened, nX * eaveFasciaSizeAtEaveReturn - nY * fasciaThickness);

      const soffitExtrusion = thickenExtrudeRailInZ(
        startRailShortened,
        miniShedPlaneOffsetted,
        soffitDepth,
        eaveReturnOverhangFromSheathing + nX * eaveFasciaSizeAtEaveReturn,
      );

      const { faces } = extrusionToBRep(soffitExtrusion);

      addGeneratedSoffit(returnSoffits, eaveType, faces, soffitExtrusion, soffitMaterial);
    }

    if (addEndDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        -xNorm * endEaveWidth,
        -yNorm * endEaveWidth,
        -zNorm * endEaveWidth,
      );

      const endRailOriginal = polyLine3New([
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        railEndX,
        railEndY,
        railEndZ,
      ]);
      const endRail = polyLine3Move(endRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? endSlope;
      const [nX, nY] = hasEndSquaredCut ? vec2Normalize(-eaveReturnRoofSlopeResolved, 1) : [0, 1];
      const soffitOffsetX = -nX * getSoffitDepth(roofSettings) + nY * eaveFasciaThicknessAtEaveReturn;
      const soffitDepth = -nY * getSoffitDepth(roofSettings) - nX * eaveFasciaThicknessAtEaveReturn;

      const miniShedPlaneOffsetted = hasEndSquaredCut
        ? planeMoveAlongNormal(planeCopy(railPlane), soffitWidth - eaveReturnOverhangFromSheathing + soffitOffsetX)
        : miniPlane;

      const endRailShortened = polyLine3Copy(endRail);
      polyLine3ExtendStart(endRailShortened, nX * eaveFasciaSize - nY * fasciaThickness);
      isShed
        ? polyLine3ExtendEnd(endRailShortened, -nY * fasciaThickness)
        : polyLine3ExtendEnd(endRailShortened, nX * eaveFasciaSizeAtEaveReturn - nY * fasciaThickness);

      const soffitExtrusion = thickenExtrudeRailInZ(
        endRailShortened,
        miniShedPlaneOffsetted,
        soffitDepth,
        eaveReturnOverhangFromSheathing + nX * eaveFasciaSizeAtEaveReturn,
      );

      const { faces } = extrusionToBRep(soffitExtrusion);

      addGeneratedSoffit(returnSoffits, eaveType, faces, soffitExtrusion, soffitMaterial);
    }
  }

  function addBoxMiniShedEndTrim(fasciaBoardPlane: Plane) {
    const [xdiff, ydiff, zdiff] = vec3Sub(rail[railElements - 3], rail[railElements - 2], 0, rail[0], rail[1], 0);
    const [xNorm, yNorm, zNorm] = vec3Normalize(xdiff, ydiff, zdiff);
    const eaveFasciaSizeAtEaveReturn = isEndSide ? eaveFasciaSize : eaveFasciaSize; //TODO: use correct eaveFasciaSize at start
    const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(fasciaBoardPlane);
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;
    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    if (addStartDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[0],
        rail[1],
        rail[2],
        xNorm * startEaveWidth,
        yNorm * startEaveWidth,
        zNorm * startEaveWidth,
      );

      const startRailOriginal = polyLine3New([rail[0], rail[1], rail[2], railEndX, railEndY, railEndZ]);
      const startRail = polyLine3Move(startRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? startSlope;

      const startTrimRail = polyLine3Copy(startRail);
      polyLine3ExtendStart(startTrimRail, -(startEaveWidth - eaveFasciaThicknessAtEaveReturn));

      const fasciaBoardEndTrim = hasStartSquaredCut
        ? squaredMiniHipEndTrimSideProfile(
            eaveFasciaSizeAtEaveReturn,
            eaveReturnOverhangFromSheathing,
            eaveReturnRoofSlopeResolved,
          )
        : plumbEaveReturnCrossSectionProfile(
            eaveFasciaSizeAtEaveReturn,
            eaveReturnOverhangFromSheathing,
            0,
            REMOVE_MINI_HIP_END_TRIM_OVERLAP,
            -REMOVE_MINI_HIP_END_TRIM_OVERLAP,
          );

      returnFasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([fasciaBoardEndTrim], startTrimRail, 0, 0, 1).faces,
          baseDescriptorNew(ViewEntityType.MINI_SHED_EAVE),
          rakeFasciaMaterial,
        ),
      );
    }

    if (addEndDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        -xNorm * endEaveWidth,
        -yNorm * endEaveWidth,
        -zNorm * endEaveWidth,
      );

      const endRailOriginal = polyLine3New([
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        railEndX,
        railEndY,
        railEndZ,
      ]);
      const endRail = polyLine3Move(endRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? endSlope;

      const endTrimRail = polyLine3ReverseCopy(endRail);
      polyLine3ExtendEnd(endTrimRail, -(endEaveWidth - eaveFasciaThicknessAtEaveReturn));

      const fasciaBoardEndTrim = hasEndSquaredCut
        ? squaredMiniHipEndTrimSideProfile(
            eaveFasciaSizeAtEaveReturn,
            eaveReturnOverhangFromSheathing,
            eaveReturnRoofSlopeResolved,
          )
        : plumbEaveReturnCrossSectionProfile(
            eaveFasciaSizeAtEaveReturn,
            eaveReturnOverhangFromSheathing,
            0,
            REMOVE_MINI_HIP_END_TRIM_OVERLAP,
            -REMOVE_MINI_HIP_END_TRIM_OVERLAP,
          );

      returnFasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([fasciaBoardEndTrim], endTrimRail, 0, 0, 1).faces,
          baseDescriptorNew(ViewEntityType.MINI_SHED_EAVE),
          rakeFasciaMaterial,
        ),
      );
    }
  }

  function addBoxMiniHipFasciaBoard(fasciaBoardPlane: Plane, isShed: boolean = true) {
    const [xdiff, ydiff, zdiff] = vec3Sub(rail[railElements - 3], rail[railElements - 2], 0, rail[0], rail[1], 0);
    const [xNorm, yNorm, zNorm] = vec3Normalize(xdiff, ydiff, zdiff);
    const eaveFasciaSizeAtEaveReturn = isEndSide ? eaveFasciaSize : eaveFasciaSize; //TODO: use correct eaveFasciaSize at start
    const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(fasciaBoardPlane);
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;
    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    if (addStartDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[0],
        rail[1],
        rail[2],
        xNorm * startEaveWidth,
        yNorm * startEaveWidth,
        zNorm * startEaveWidth,
      );

      const startRailOriginal = polyLine3New([rail[0], rail[1], rail[2], railEndX, railEndY, railEndZ]);
      const frontOnlyRail = polyLine3Move(startRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? startSlope;

      const [railTrimEndX, railTrimEndY, railTrimEndZ] = vec3Add(
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        nxFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nyFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nzFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
      );

      const fullWrapRail = polyLine3New([
        frontOnlyRail[0],
        frontOnlyRail[1],
        frontOnlyRail[2],
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        railTrimEndX,
        railTrimEndY,
        railTrimEndZ,
      ]);

      const fasciaBoard = hasStartSquaredCut
        ? squaredEaveReturnCrossSectionProfile(
            eaveFasciaSizeAtEaveReturn,
            eaveReturnRoofSlopeResolved,
            eaveFasciaThicknessAtEaveReturn,
          )
        : plumbEaveReturnCrossSectionProfile(eaveFasciaSizeAtEaveReturn, eaveFasciaThicknessAtEaveReturn);

      const hipShedFasciaBoardRail = isShed ? frontOnlyRail : fullWrapRail;

      const fasciaRails = clipRail3({
        rail: hipShedFasciaBoardRail,
        options: {
          clippingNodes: [clippingNodeNew(insideBSP)],
          coplanarsToFront: true,
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
      });

      for (const fasciaRail of fasciaRails) {
        if (!hasStartSquaredCut) {
          polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
        }
        returnFasciaBoards.push(
          faceArrayViewEntityNew(
            extrudeFaceAlongPolyLine3([fasciaBoard], fasciaRail, 0, 0, 1).faces,
            baseDescriptorNew(ViewEntityType.MINI_HIP_EAVE),
            rakeFasciaMaterial,
          ),
        );
      }
    }

    if (addEndDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        -xNorm * endEaveWidth,
        -yNorm * endEaveWidth,
        -zNorm * endEaveWidth,
      );

      const endRailOriginal = polyLine3New([
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        railEndX,
        railEndY,
        railEndZ,
      ]);
      const frontOnlyRail = polyLine3Move(endRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? endSlope;

      const [railTrimEndX, railTrimEndY, railTrimEndZ] = vec3Add(
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        nxFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nyFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nzFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
      );

      const fullWrapRail = polyLine3New([
        frontOnlyRail[0],
        frontOnlyRail[1],
        frontOnlyRail[2],
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        railTrimEndX,
        railTrimEndY,
        railTrimEndZ,
      ]);

      const fasciaBoard = hasEndSquaredCut
        ? squaredEaveReturnCrossSectionProfile(
            eaveFasciaSizeAtEaveReturn,
            eaveReturnRoofSlopeResolved,
            eaveFasciaThicknessAtEaveReturn,
          )
        : plumbEaveReturnCrossSectionProfile(eaveFasciaSizeAtEaveReturn, eaveFasciaThicknessAtEaveReturn);

      const hipShedFasciaBoardRail = isShed ? frontOnlyRail : fullWrapRail;

      const fasciaRails = clipRail3({
        rail: polyLine3Reverse(hipShedFasciaBoardRail),
        options: {
          clippingNodes: [clippingNodeNew(insideBSP)],
          coplanarsToFront: true,
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
      });

      for (const fasciaRail of fasciaRails) {
        if (!hasEndSquaredCut) {
          polyLine3ExtendBothEnds(fasciaRail, -REMOVE_RAKE_EAVES_OVERLAP_EPSILON);
        }
        returnFasciaBoards.push(
          faceArrayViewEntityNew(
            extrudeFaceAlongPolyLine3([fasciaBoard], fasciaRail, 0, 0, 1).faces,
            baseDescriptorNew(ViewEntityType.MINI_HIP_EAVE),
            rakeFasciaMaterial,
          ),
        );
      }
    }
  }

  function addBoxMiniHipShedFasciaTopTrim(fasciaBoardPlane: Plane, isShed: boolean = true) {
    const { uX, uY, uZ } = railPlane;
    const [xdiff, ydiff, zdiff] = vec3Sub(rail[railElements - 3], rail[railElements - 2], 0, rail[0], rail[1], 0);
    const [xNorm, yNorm, zNorm] = vec3Normalize(xdiff, ydiff, zdiff);
    const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(fasciaBoardPlane);
    const dist = soffitWidth - eaveReturnOverhangFromSheathing;
    const [xRailOffset, yRailOffset, zRailOffset] = [
      dist * nxFasciaBoardPlaneNorm,
      dist * nyFasciaBoardPlaneNorm,
      dist * nzFasciaBoardPlaneNorm,
    ];

    if (addStartDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[0],
        rail[1],
        rail[2],
        xNorm * startEaveWidth,
        yNorm * startEaveWidth,
        zNorm * startEaveWidth,
      );

      const startRailOriginal = polyLine3New([rail[0], rail[1], rail[2], railEndX, railEndY, railEndZ]);
      const frontOnlyRail = polyLine3Move(startRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? startSlope;

      const [railTrimEndX, railTrimEndY, railTrimEndZ] = vec3Add(
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        nxFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nyFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nzFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
      );

      const fullWrapRail = polyLine3New([
        frontOnlyRail[0],
        frontOnlyRail[1],
        frontOnlyRail[2],
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        railTrimEndX,
        railTrimEndY,
        railTrimEndZ,
      ]);

      const hipShedTrimRail = isShed ? frontOnlyRail : fullWrapRail;

      if (includeFasciaTopTrim && dist < -FASCIA_BOARD_GENERATION_THRESHOLD) {
        const shiftForGelingFasciaTopTrim = hasStartSquaredCut ? -fasciaTopTrimSize : 0;
        const [startRailEndPtX, startRailEndPtY, startRailEndPtZ] = vec3Sub(
          rail[0],
          rail[1],
          rail[2],
          shiftForGelingFasciaTopTrim * nxFasciaBoardPlaneNorm,
          shiftForGelingFasciaTopTrim * nyFasciaBoardPlaneNorm,
          shiftForGelingFasciaTopTrim * nzFasciaBoardPlaneNorm,
        );
        const extensionRail = polyLine3New([
          startRailEndPtX,
          startRailEndPtY,
          startRailEndPtZ,
          hipShedTrimRail[0],
          hipShedTrimRail[1],
          hipShedTrimRail[2],
          hipShedTrimRail[0] + uX * fasciaTopTrimSize, //point in the plane of the front fascia board
          hipShedTrimRail[1] + uY * fasciaTopTrimSize, //point in the plane of the front fascia board
          hipShedTrimRail[2] + uZ * fasciaTopTrimSize, //point in the plane of the front fascia board
        ]);
        addFasciaTrim(
          polyLine3Reverse(
            extensionRail,
          ) /*doing reverse copy here because the trim needs to be outside of the startRail*/,
          FASCIA_TOP_TRIM_THICKNESS,
          fasciaTopTrimSize,
          fasciaTopTrims,
          hasStartSquaredCut,
          startSlope,
          fasciaTopTrimMaterial,
        );
      }

      const [hipShedTrimRailStartX, hipShedTrimRailStartY, hipShedTrimRailStartZ] = vec3Add(
        hipShedTrimRail[0],
        hipShedTrimRail[1],
        hipShedTrimRail[2],
        nxFasciaBoardPlaneNorm * fasciaTopTrimSize,
        nyFasciaBoardPlaneNorm * fasciaTopTrimSize,
        nzFasciaBoardPlaneNorm * fasciaTopTrimSize,
      );

      //extending hip-shed for trims to gel together
      const extendedHipShedTrimRail = polyLine3New([
        hipShedTrimRailStartX,
        hipShedTrimRailStartY,
        hipShedTrimRailStartZ,
        ...hipShedTrimRail,
      ]);

      if (includeEaveReturnFasciaTopTrim) {
        addFasciaTrim(
          polyLine3Reverse(
            extendedHipShedTrimRail,
          ) /*doing reverse copy here because the trim needs to be outside of the startRail*/,
          FASCIA_TOP_TRIM_THICKNESS,
          fasciaTopTrimSize,
          fasciaTopTrims,
          hasStartSquaredCut,
          eaveReturnRoofSlopeResolved,
          fasciaTopTrimMaterial,
        );
      }
    }

    if (addEndDetails) {
      const [railEndX, railEndY, railEndZ] = vec3Add(
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        -xNorm * endEaveWidth,
        -yNorm * endEaveWidth,
        -zNorm * endEaveWidth,
      );

      const endRailOriginal = polyLine3New([
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
        railEndX,
        railEndY,
        railEndZ,
      ]);
      const frontOnlyRail = polyLine3Move(endRailOriginal, xRailOffset, yRailOffset, zRailOffset);
      const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? endSlope;

      const [railTrimEndX, railTrimEndY, railTrimEndZ] = vec3Add(
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        nxFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nyFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
        nzFasciaBoardPlaneNorm * eaveReturnOverhangFromSheathing,
      );

      const fullWrapRail = polyLine3New([
        frontOnlyRail[0],
        frontOnlyRail[1],
        frontOnlyRail[2],
        frontOnlyRail[frontOnlyRail.length - 3],
        frontOnlyRail[frontOnlyRail.length - 2],
        frontOnlyRail[frontOnlyRail.length - 1],
        railTrimEndX,
        railTrimEndY,
        railTrimEndZ,
      ]);

      const hipShedTrimRail = isShed ? frontOnlyRail : fullWrapRail;

      if (includeFasciaTopTrim && dist < -FASCIA_BOARD_GENERATION_THRESHOLD) {
        const shiftForGelingFasciaTopTrim = hasEndSquaredCut ? -fasciaTopTrimSize : 0;
        const [startRailEndPtX, startRailEndPtY, startRailEndPtZ] = vec3Sub(
          rail[railElements - 3],
          rail[railElements - 2],
          rail[railElements - 1],
          shiftForGelingFasciaTopTrim * nxFasciaBoardPlaneNorm,
          shiftForGelingFasciaTopTrim * nyFasciaBoardPlaneNorm,
          shiftForGelingFasciaTopTrim * nzFasciaBoardPlaneNorm,
        );
        const extensionRail = polyLine3New([
          startRailEndPtX,
          startRailEndPtY,
          startRailEndPtZ,
          hipShedTrimRail[0],
          hipShedTrimRail[1],
          hipShedTrimRail[2],
          hipShedTrimRail[0] - uX * fasciaTopTrimSize, //point in the plane of the front fascia board
          hipShedTrimRail[1] - uY * fasciaTopTrimSize, //point in the plane of the front fascia board
          hipShedTrimRail[2] - uZ * fasciaTopTrimSize, //point in the plane of the front fascia board
        ]);

        addFasciaTrim(
          extensionRail,
          FASCIA_TOP_TRIM_THICKNESS,
          fasciaTopTrimSize,
          fasciaTopTrims,
          hasEndSquaredCut,
          endSlope,
          fasciaTopTrimMaterial,
        );
      }

      const [hipShedTrimRailStartX, hipShedTrimRailStartY, hipShedTrimRailStartZ] = vec3Add(
        hipShedTrimRail[0],
        hipShedTrimRail[1],
        hipShedTrimRail[2],
        nxFasciaBoardPlaneNorm * fasciaTopTrimSize,
        nyFasciaBoardPlaneNorm * fasciaTopTrimSize,
        nzFasciaBoardPlaneNorm * fasciaTopTrimSize,
      );

      //extending hip-shed for trims to gel together
      const extendedHipShedTrimRail = polyLine3New([
        hipShedTrimRailStartX,
        hipShedTrimRailStartY,
        hipShedTrimRailStartZ,
        ...hipShedTrimRail,
      ]);

      if (includeEaveReturnFasciaTopTrim) {
        addFasciaTrim(
          extendedHipShedTrimRail,
          FASCIA_TOP_TRIM_THICKNESS,
          fasciaTopTrimSize,
          fasciaTopTrims,
          hasEndSquaredCut,
          eaveReturnRoofSlopeResolved,
          fasciaTopTrimMaterial,
        );
      }
    }
  }

  const isValidForFullReturn = railElements > 6 && Math.abs(startZ - endZ) < MAX_HEIGHT_DIFFERENCE_FOR_FULL_RETURN;

  if (eaveType === EaveType.BOXED_BOX) {
    //offseting railplane for reducing material overlap
    const railPlaneOffset = planeMoveAlongNormal(planeCopy(railPlane), fasciaThickness);

    if (startSlope > 0 && addStartDetails) {
      const startBoxFascia = hasStartSquaredCut
        ? squaredSlopedBoxEaveProfile(eaveFasciaSize, startSlope, startEaveWidth, 0, MULTIGABLE_BSP_EPSILON)
        : boxEaveProfile(eaveFasciaSize, startSlope, startEaveWidth, 0, MULTIGABLE_BSP_EPSILON);

      polyLine2Move(startBoxFascia, 0, startZ - minZ);

      fasciaBoards.push(
        faceArrayViewEntityNew(
          clipExtrusion(extrudePolyLine2AlongNormal(startBoxFascia, railPlane, fasciaThickness)),
          baseDescriptorNew(ViewEntityType.BOX_EAVE),
          rakeFasciaMaterial,
        ),
      );

      const startBoxSoffit = hasStartSquaredCut
        ? squaredSlopedBoxEaveProfile(soffitDepth, startSlope, startEaveWidth, 0, fasciaThickness)
        : boxEaveProfile(soffitDepth, startSlope, startEaveWidth, 0, fasciaThickness);

      polyLine2Move(startBoxSoffit, 0, startZ - minZ);

      const soffitExtrusion = extrudePolyLine2AlongNormal(
        startBoxSoffit,
        railPlaneOffset,
        endRoofOverhang - wallOffsetFromOverhangDatum,
      );

      addGeneratedSoffit(soffits, eaveType, clipExtrusion(soffitExtrusion), soffitExtrusion, soffitMaterial);

      const [nX, nY] = hasStartSquaredCut ? vec2Normalize(-startSlope, 1) : [0, 1];
      const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * fasciaThickness;

      if (hasFriezeReturnAtStart) {
        addEaveReturnFriezeBoard(friezeBoards, eaveReturnDistance, true, soffitDepthSquaredEave);
      } else {
        addFriezeBoardExtensionAtSide(
          friezeBoards,
          Math.max(startEaveWidth, soffitDepth * Math.sin(startSlopeAngle / 2)),
          true,
        );
      }
    }

    if (endSlope > 0 && addEndDetails) {
      const endBoxFasciaWithoutReflectionCorrection = hasEndSquaredCut
        ? squaredSlopedBoxEaveProfile(eaveFasciaSize, endSlope, endEaveWidth, 0, MULTIGABLE_BSP_EPSILON)
        : boxEaveProfile(eaveFasciaSize, endSlope, endEaveWidth, 0, MULTIGABLE_BSP_EPSILON);

      const endBoxFascia = polyLine2ReflectOverYAxis(endBoxFasciaWithoutReflectionCorrection);

      polyLine2Move(endBoxFascia, span, endZ - minZ);

      //creating rake fascia material at end
      fasciaBoards.push(
        faceArrayViewEntityNew(
          clipExtrusion(extrudePolyLine2AlongNormal(endBoxFascia, railPlane, fasciaThickness)),
          baseDescriptorNew(ViewEntityType.BOX_EAVE),
          rakeFasciaMaterial,
        ),
      );

      const endBoxSoffitWithoutReflectionCorrection = hasEndSquaredCut
        ? squaredSlopedBoxEaveProfile(soffitDepth, endSlope, endEaveWidth, 0, fasciaThickness)
        : boxEaveProfile(soffitDepth, endSlope, endEaveWidth, 0, fasciaThickness);

      const endBoxSoffit = polyLine2ReflectOverYAxis(endBoxSoffitWithoutReflectionCorrection);

      polyLine2Move(endBoxSoffit, span, endZ - minZ);

      const soffitExtrusion = extrudePolyLine2AlongNormal(
        endBoxSoffit,
        railPlaneOffset,
        endRoofOverhang - wallOffsetFromOverhangDatum,
      );

      addGeneratedSoffit(soffits, eaveType, clipExtrusion(soffitExtrusion), soffitExtrusion, soffitMaterial);

      const [nX, nY] = hasEndSquaredCut ? vec2Normalize(-endSlope, 1) : [0, 1];
      const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * fasciaThickness;

      if (hasFriezeReturnAtEnd) {
        addEaveReturnFriezeBoard(friezeBoards, eaveReturnDistance, false, soffitDepthSquaredEave);
      } else {
        addFriezeBoardExtensionAtSide(
          friezeBoards,
          Math.max(endEaveWidth, soffitDepth * Math.sin(endSlopeAngle / 2)),
          false,
        );
      }
    }

    await addRakeBracketsIfNeeded();
  } else if (eaveType === EaveType.BOXED_MINI_HIP) {
    if (includeElementBasedOnOrientation(invRailPlane, miniHipOrientation)) {
      const miniHipPlane = planeMoveAlongNormal(
        planeCopy(railPlane),
        soffitWidth - eaveReturnOverhangFromSheathing + fasciaThickness,
      );
      const fasciaBoardPlane = planeMoveAlongNormal(
        planeCopy(railPlane),
        soffitWidth - eaveReturnOverhangFromSheathing,
      );

      const hipRoofOpts: MiniHipRoofOptions = {
        rail,
        railPlane: fasciaBoardPlane,
        rails: subRoof.rails,
        fasciaSize: eaveFasciaSize,
        soffitWidth,
        eaveReturnOverhang: eaveReturnOverhangFromSheathing,
        eaveWidth: startEaveWidth,
        eaveSlope: startSlope,
        slope: eaveReturnRoofSlope ?? startSlope,
        roofTopFaces: returnTopFaces,
        isStart: true,
        roofTopBSP,
        eaveReturnRoofMaterial,
        offsetZ: startZ - minZ,
      };

      addBoxMiniSoffit(miniHipPlane, fasciaBoardPlane, false);
      addBoxMiniHipFasciaBoard(fasciaBoardPlane, false);
      addBoxMiniHipShedFasciaTopTrim(fasciaBoardPlane, false);
      addMiniHipShedFasciaBoardExtensions();

      const eaveFasciaSizeAtEaveReturn = isEndSide ? eaveFasciaSize : eaveFasciaSize; //TODO: use correct eaveFasciaSize at start
      const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start

      if (addStartDetails) {
        const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? startSlope;
        const [nX, nY] = hasStartSquaredCut ? vec2Normalize(-eaveReturnRoofSlopeResolved, 1) : [0, 1];
        const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * eaveFasciaThicknessAtEaveReturn;

        gableMiniHipRoof({ ...hipRoofOpts, eaveReturnRoofMaterial });

        if (hasFriezeReturnAtStart) {
          addEaveReturnFriezeBoard(
            returnFriezeBoards,
            eaveReturnDistance + nX * eaveFasciaSizeAtEaveReturn,
            true,
            soffitDepthSquaredEave,
          );
        } else {
          addFriezeBoardExtensionAtSide(
            returnFriezeBoards,
            Math.max(startEaveWidth, soffitDepth * Math.sin(startSlopeAngle / 2)),
            true,
          );
        }
      }

      if (addEndDetails) {
        const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? endSlope;
        const [nX, nY] = hasEndSquaredCut ? vec2Normalize(-eaveReturnRoofSlopeResolved, 1) : [0, 1];
        const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * eaveFasciaThicknessAtEaveReturn;

        gableMiniHipRoof({
          ...hipRoofOpts,
          isStart: false,
          slope: eaveReturnRoofSlope ?? endSlope,
          eaveSlope: endSlope,
          eaveWidth: endEaveWidth,
          offsetZ: 0,
          eaveReturnRoofMaterial,
        });

        if (hasFriezeReturnAtEnd) {
          addEaveReturnFriezeBoard(
            returnFriezeBoards,
            eaveReturnDistance + nX * eaveFasciaSizeAtEaveReturn,
            false,
            soffitDepthSquaredEave,
          );
        } else {
          addFriezeBoardExtensionAtSide(
            returnFriezeBoards,
            Math.max(endEaveWidth, soffitDepth * Math.sin(endSlopeAngle / 2)),
            false,
          );
        }
      }

      clipEaveReturnEntities({
        edge,
        eaveReturnOverhang: eaveReturnOverhangFromSheathing,
        subRoof,
        subRoofs,
        rail,
        railPlane,
        insideBSP,
        startEaveCutType,
        endEaveCutType,
        addStartDetails,
        addEndDetails,
        topFaces: returnTopFaces,
        fasciaBoards: returnFasciaBoards,
        soffits: returnSoffits,
        friezeBoards: returnFriezeBoards,
        outTopFaces: roofTopFaces,
        outFasciaBoards: fasciaBoards,
        outSoffits: soffits,
        outFriezeBoards: friezeBoards,
      });
    }

    await addRakeBracketsIfNeeded();
  } else if (eaveType === EaveType.BOXED_MINI_SHED) {
    if (includeElementBasedOnOrientation(invRailPlane, miniHipOrientation)) {
      const miniShedPlane = planeMoveAlongNormal(
        planeCopy(railPlane),
        soffitWidth - eaveReturnOverhangFromSheathing + fasciaThickness,
      );

      const fasciaBoardPlane = planeMoveAlongNormal(
        planeCopy(railPlane),
        soffitWidth - eaveReturnOverhangFromSheathing,
      );

      const shedRoofOpts: MiniShedRoofOptions = {
        rail,
        railPlane: fasciaBoardPlane,
        rails: subRoof.rails,
        fasciaSize: eaveFasciaSize,
        eaveReturnOverhang: eaveReturnOverhangFromSheathing,
        soffitWidth,
        eaveWidth: startEaveWidth,
        eaveSlope: startSlope,
        slope: eaveReturnRoofSlope ?? startSlope,
        fasciaBoards: returnFasciaBoards,
        roofTopFaces: returnTopFaces,
        isStart: true,
        roofTopBSP,
        eaveReturnRoofMaterial,
        offsetZ: startZ - minZ,
        fasciaMaterial: rakeFasciaMaterial,
      };

      addBoxMiniSoffit(miniShedPlane, fasciaBoardPlane);
      addBoxMiniHipFasciaBoard(fasciaBoardPlane);
      addBoxMiniShedEndTrim(fasciaBoardPlane);
      addBoxMiniHipShedFasciaTopTrim(fasciaBoardPlane);
      addMiniHipShedFasciaBoardExtensions();

      const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start

      if (addStartDetails) {
        const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? startSlope;
        const [nX, nY] = hasStartSquaredCut ? vec2Normalize(-eaveReturnRoofSlopeResolved, 1) : [0, 1];
        const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * eaveFasciaThicknessAtEaveReturn;

        gableMiniShedRoof({ ...shedRoofOpts });

        if (hasFriezeReturnAtStart) {
          addEaveReturnFriezeBoard(returnFriezeBoards, eaveReturnDistance, true, soffitDepthSquaredEave);
        } else {
          addFriezeBoardExtensionAtSide(
            returnFriezeBoards,
            Math.max(startEaveWidth, soffitDepth * Math.sin(startSlopeAngle / 2)),
            true,
          );
        }
      }

      if (addEndDetails) {
        const eaveReturnRoofSlopeResolved = eaveReturnRoofSlope ?? endSlope;
        const [nX, nY] = hasEndSquaredCut ? vec2Normalize(-eaveReturnRoofSlopeResolved, 1) : [0, 1];
        const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * eaveFasciaThicknessAtEaveReturn;

        gableMiniShedRoof({
          ...shedRoofOpts,
          isStart: false,
          slope: eaveReturnRoofSlope ?? endSlope,
          eaveSlope: endSlope,
          eaveWidth: endEaveWidth,
          offsetZ: 0,
        });

        if (hasFriezeReturnAtEnd) {
          addEaveReturnFriezeBoard(returnFriezeBoards, eaveReturnDistance, false, soffitDepthSquaredEave);
        } else {
          addFriezeBoardExtensionAtSide(
            returnFriezeBoards,
            Math.max(endEaveWidth, soffitDepth * Math.sin(endSlopeAngle / 2)),
            false,
          );
        }
      }

      clipEaveReturnEntities({
        edge,
        eaveReturnOverhang: eaveReturnOverhangFromSheathing,
        subRoof,
        subRoofs,
        rail,
        railPlane,
        insideBSP,
        startEaveCutType,
        endEaveCutType,
        addStartDetails,
        addEndDetails,
        topFaces: returnTopFaces,
        fasciaBoards: returnFasciaBoards,
        soffits: returnSoffits,
        friezeBoards: returnFriezeBoards,
        outTopFaces: roofTopFaces,
        outFasciaBoards: fasciaBoards,
        outSoffits: soffits,
        outFriezeBoards: friezeBoards,
      });
    }

    await addRakeBracketsIfNeeded();
  } else if (eaveType === EaveType.BOXED_OPEN || eaveType === EaveType.ANGLED_ENCLOSED_OPEN) {
    await addRakeBracketsIfNeeded();
    addFriezeBoardExtensionAtSide(friezeBoards, soffitDepth * Math.sin(startSlopeAngle / 2), true);
    addFriezeBoardExtensionAtSide(friezeBoards, soffitDepth * Math.sin(endSlopeAngle / 2), false);
  } else if (eaveType === EaveType.BOXED_FULL_RETURN) {
    if (isValidForFullReturn && includeElementBasedOnOrientation(invRailPlane, fullReturnOrientation)) {
      const hipRoofReturnPlane = planeMoveAlongNormal(
        planeCopy(railPlane),
        soffitWidth - eaveReturnOverhangFromSheathing + BSP_EPSILON,
      );
      const [nxFasciaBoardPlaneNorm, nyFasciaBoardPlaneNorm, nzFasciaBoardPlaneNorm] = planeNormal(railPlane);
      const dist = soffitWidth - eaveReturnOverhangFromSheathing;
      const [xRailOffset, yRailOffset, zRailOffset] = [
        dist * nxFasciaBoardPlaneNorm,
        dist * nyFasciaBoardPlaneNorm,
        dist * nzFasciaBoardPlaneNorm,
      ];

      const baseRailOriginal = polyLine3New([
        rail[0],
        rail[1],
        rail[2],
        rail[railElements - 3],
        rail[railElements - 2],
        rail[railElements - 1],
      ]);
      const baseRail = polyLine3Move(baseRailOriginal, xRailOffset, yRailOffset, zRailOffset);

      addBoxFullReturnFasciaTrimOptions(hipRoofReturnPlane);
      addBoxedFullReturnSoffit();
      addFasciaAndRafterTailExtension();
      addBoxFullReturnFasciaBoardExtensions();

      const endEaveReturnSlopeResolved = isEndSide
        ? eaveReturnRoofSlope ?? endSlope
        : eaveReturnRoofSlope ?? startSlope;

      const [nX, nY] = hasSquaredCut ? vec2Normalize(-endEaveReturnSlopeResolved, 1) : [0, 1];
      const eaveFasciaThicknessAtEaveReturn = isEndSide ? fasciaThickness : fasciaThickness; //TODO: use correct fasciaThickness at start
      const soffitDepthSquaredEave = nY * getSoffitDepth(roofSettings) + nX * eaveFasciaThicknessAtEaveReturn;

      // frieze board
      if (hasFriezeReturnAtStart) {
        addEaveReturnFriezeBoard(returnFriezeBoards, span / 2, true, soffitDepthSquaredEave);
      } else {
        addFriezeBoardExtensionAtSide(
          returnFriezeBoards,
          Math.max(span / 2, soffitDepth * Math.sin(startSlopeAngle / 2)),
          true,
        );
      }

      if (hasFriezeReturnAtEnd) {
        addEaveReturnFriezeBoard(returnFriezeBoards, span / 2, false, soffitDepthSquaredEave);
      } else {
        addFriezeBoardExtensionAtSide(
          returnFriezeBoards,
          Math.max(span / 2, soffitDepth * Math.sin(endSlopeAngle / 2)),
          false,
        );
      }

      const finalParentSlope = 0.75;

      gableFullReturnHipRoof({
        rail: baseRail,
        railPlane: hipRoofReturnPlane,
        rails: subRoof.rails,
        eaveType,
        eaveReturnOverhang: eaveReturnOverhangFromSheathing,
        soffitWidth,
        soffitMaterial,
        startSlope,
        endSlope,
        parentSlope: finalParentSlope,
        addRafterTailExtension,
        eaveReturnRoofSlope,
        eaveReturnRoofMaterial,
        rafterTailExtensionEdgeOffset,
        rafterTailExtension,
        rafterTailDepth,
        rafterTailClosed,
        includeFasciaTopTrim: includeFasciaTopTrim && includeEaveReturnFasciaTopTrim,
        fasciaTopTrimSize,
        fasciaTopTrims,
        fasciaBoards: returnFasciaBoards,
        soffits: returnSoffits,
        roofTopFaces: returnTopFaces,
        fasciaSize: eaveFasciaSize,
        fasciaTopTrimMaterial,
        fasciaThickness,
        rakeFasciaMaterial,
        hasSquareCutEave: hasSquaredCut,
      });

      clipEaveReturnEntities({
        edge,
        eaveReturnOverhang: eaveReturnOverhangFromSheathing,
        subRoof,
        subRoofs,
        rail,
        railPlane,
        insideBSP,
        startEaveCutType,
        endEaveCutType,
        addStartDetails,
        addEndDetails,
        topFaces: returnTopFaces,
        fasciaBoards: returnFasciaBoards,
        soffits: returnSoffits,
        friezeBoards: returnFriezeBoards,
        outTopFaces: roofTopFaces,
        outFasciaBoards: fasciaBoards,
        outSoffits: soffits,
        outFriezeBoards: friezeBoards,
      });
    }

    await addRakeBracketsIfNeeded();
  } else if (eaveType === EaveType.ANGLED_OPEN_OPEN) {
    await addRakeBracketsIfNeeded();
  }

  addGableVentIfNeeded();
}

function addGeneratedSoffit(
  soffitsOut: GeneratedRoofSoffit[],
  eaveType: EaveType,
  faces: Face[],
  { dirX, dirY, dirZ }: { dirX: number; dirY: number; dirZ: number },
  material: ExteriorMaterial | undefined,
  viewEntityType: ViewEntityType = ViewEntityType.EAVE_SOFFIT,
): void {
  for (const face of faces) {
    const [, , nz] = planeNormal(face.plane);

    tagFace(face, TAG_CONDITION_GABLE);

    if (nz < -DIST_EPSILON) {
      tagFace(face, TAG_POSITION_BOTTOM);
    }
  }

  soffitsOut.push(
    makeSoffit({
      ...faceArrayViewEntityNew(faces, baseDescriptorNew(viewEntityType), material),
      direction: [dirX, dirY, dirZ],
      eaveType,
    }),
  );
}

function polyLine3ShiftThickenWithDirection(pl: PolyLine3, offsetX: number, offsetY: number, offsetZ: number) {
  if (!(!polyLine3IsClosed(pl))) {
    console.assert(false, "cannot shift thicken a closed polyline3");
  }

  const len = pl.length * 2 + 3;

  const final = polyLine3New(len);
  const rev = polyLine3Move(polyLine3CopyReversed(pl), offsetX, offsetY, offsetZ);

  final.set(pl);
  final.set(rev, pl.length);
  final[len - 3] = pl[0];
  final[len - 2] = pl[1];
  final[len - 1] = pl[2];

  return final;
}

function planeByGableRail(pl: PolyLine3): Plane {
  const lastX = pl[pl.length - 3];
  const lastY = pl[pl.length - 2];
  const lastZ = pl[pl.length - 1];

  const [uX, uY] = line2Tangent(pl[0], pl[1], lastX, lastY);
  return planeNew(pl[0], pl[1], Math.min(pl[2], lastZ), uX, uY, 0, 0, 0, 1);
}

function thickenExtrudeRailInZ(
  rail: PolyLine3,
  basePlane: Plane,
  shiftDist: number,
  extrudeDist: number,
  offsetPlaneDist: number = 0,
) {
  const rail2 = planeProjectPolyLine3(basePlane, polyLine3ShiftThickenWithDirection(rail, 0, 0, shiftDist));
  if (offsetPlaneDist !== 0) {
    basePlane = planeMoveAlongNormal(planeCopy(basePlane), offsetPlaneDist);
  }
  return extrudePolyLine2AlongNormal(rail2, basePlane, extrudeDist);
}

interface MiniShedRoofOptions {
  readonly rail: PolyLine3;
  readonly railPlane: Plane;
  readonly rails: RoofRails;
  readonly fasciaSize: number;
  readonly eaveReturnOverhang: number;
  readonly soffitWidth: number;
  readonly eaveWidth: number;
  readonly slope: number;
  readonly eaveSlope: number;
  readonly fasciaBoards: FaceArrayViewEntity[];
  readonly roofTopFaces: FaceArrayViewEntity[];
  readonly isStart: boolean;
  readonly roofTopBSP: NodeBSP | null;
  readonly eaveReturnRoofMaterial?: ExteriorMaterial;
  readonly fasciaMaterial?: ExteriorMaterial;
  readonly offsetZ: number;
}

function gableMiniShedRoof({
  rail,
  railPlane,
  rails,
  fasciaSize: eaveFasciaSize,
  eaveReturnOverhang,
  soffitWidth,
  eaveWidth,
  slope,
  eaveSlope,
  fasciaBoards,
  roofTopFaces,
  isStart,
  roofTopBSP,
  eaveReturnRoofMaterial,
  offsetZ,
  fasciaMaterial,
}: MiniShedRoofOptions) {
  slope = Math.max(slope, 0.01);

  const basePlane = planeCopy(railPlane);
  if (!isStart) {
    const reprojectedRail = polyLine3OnPlane(planeProjectPolyLine3(railPlane, rail), railPlane);
    if (reprojectedRail) {
      const [x, y, z] = polyLine3EvalEdge(reprojectedRail, polyLine3EdgeCount(reprojectedRail) - 1, 1, true);
      basePlane.originX = x;
      basePlane.originY = y;
      basePlane.originZ = z;
    } else {
      return;
    }
  }

  const [nx, ny, nz] = planeNormal(railPlane);

  basePlane.vX = -nx;
  basePlane.vY = -ny;
  basePlane.vZ = -nz;

  planeMove(basePlane, 0, 0, offsetZ);

  const peakPlane = planeCopyMoved(basePlane, 0, 0, slope * eaveReturnOverhang);

  const diff = eaveReturnOverhang - soffitWidth;
  const eaveAngle = Math.abs(Math.atan(eaveSlope));
  const eaveOffset = diff < DIST_EPSILON ? eaveFasciaSize / Math.sin(eaveAngle) : 0;

  // eave return rails
  const outerRail = polyLine2ByTwoPoints(eaveOffset, 0, eaveWidth, 0);
  const innerRail = polyLine2New([eaveOffset, -eaveReturnOverhang, eaveWidth, -eaveReturnOverhang]);
  const peakRail = polyLine2New([eaveOffset, -eaveReturnOverhang, eaveWidth, -eaveReturnOverhang]);

  if (polyLine2Length(outerRail) < DIST_EPSILON && polyLine2Length(innerRail) < DIST_EPSILON) {
    return;
  }

  if (!isStart) {
    polyLine2Move(outerRail, -eaveWidth - eaveOffset, 0);
    polyLine2Move(innerRail, -eaveWidth - eaveOffset, 0);
    polyLine2Move(peakRail, -eaveWidth - eaveOffset, 0);
  }
  // preserve rails for later use (measures)
  rails.returnEaveRails.push(
    polyLine3OnPlane(polyLine2ByTwoPoints(outerRail[0], outerRail[1], outerRail[2], outerRail[3]), basePlane),
  );
  rails.returnPeakRails.push(
    polyLine3OnPlane(polyLine2ByTwoPoints(peakRail[0], peakRail[1], peakRail[2], peakRail[3]), peakPlane),
  );

  const ext = extrudeAlongThreeRails(peakRail, outerRail, innerRail, basePlane, peakPlane);

  const clipOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(roofTopBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const capFaces =
    (isStart && ext.endFace) || (!isStart && ext.startFace)
      ? clipFace({
          face: isStart ? (ext.endFace as Face) : (ext.startFace as Face),
          options: clipOptions,
        })
      : [];

  const roofFaces = clipFaces({
    faces: ext.leftFaces,
    options: clipOptions,
  });

  addEaveReturnRoofTopExtensions({
    basePlane,
    eaveReturnOverhang,
    soffitWidth,
    slope,
    eaveSlope,
    isStart,
    faces: roofFaces,
  });

  const tag: BuildingGeomTag = { type: "CONDITION", condition: "GABLE_MINI_SHED_ROOF" };

  roofTopFaces.push(
    faceArrayViewEntityNew(
      tagFaces(roofFaces, tag),
      baseDescriptorNew(ViewEntityType.MINI_SHED_ROOF),
      eaveReturnRoofMaterial,
    ),
  );
  fasciaBoards.push(faceArrayViewEntityNew(capFaces, baseDescriptorNew(ViewEntityType.MINI_SHED_EAVE), fasciaMaterial));
}

interface AddEaveReturnRoofTopExtensionsOptions {
  readonly basePlane: Plane;
  readonly eaveReturnOverhang: number;
  readonly soffitWidth: number;
  readonly slope: number;
  readonly eaveSlope: number;
  readonly isStart: boolean;
  readonly faces: Face[];
}

function addEaveReturnRoofTopExtensions({
  basePlane,
  eaveReturnOverhang,
  soffitWidth,
  slope,
  eaveSlope,
  isStart,
  faces,
}: AddEaveReturnRoofTopExtensionsOptions) {
  assert(Math.abs(eaveSlope) > DIST_EPSILON, "Zero eave slopes should be already handled!");

  const diff = eaveReturnOverhang - soffitWidth;

  if (diff < DIST_EPSILON) {
    return;
  }

  const peakHeight = slope * diff;
  const peakPlane = planeCopyMoved(basePlane, 0, 0, peakHeight);
  const sign = isStart ? 1 : -1;

  const [x0, y0, z0] = planeEval(basePlane, 0, 0);
  const [x1, y1, z1] = planeEval(basePlane, 0, -diff);
  const [x2, y2, z2] = planeEval(peakPlane, (sign * slope * diff) / eaveSlope, -diff);

  const face = faceByThreePoints(x0, y0, z0, x1, y1, z1, x2, y2, z2);

  if (!face) {
    return;
  }

  if (!isStart) {
    faceReverse(face);
  }

  faces.push(face);
}

interface MiniHipRoofOptions {
  readonly rail: PolyLine3;
  readonly railPlane: Plane;
  readonly rails: RoofRails;
  readonly fasciaSize: number;
  readonly soffitWidth: number;
  readonly eaveReturnOverhang: number;
  readonly eaveWidth: number;
  readonly eaveSlope: number;
  readonly slope: number;
  readonly roofTopFaces: FaceArrayViewEntity[];
  readonly isStart: boolean;
  readonly roofTopBSP: NodeBSP | null;
  readonly eaveReturnRoofMaterial?: ExteriorMaterial;
  readonly offsetZ: number;
}

function gableMiniHipRoof({
  rail,
  railPlane,
  rails,
  fasciaSize,
  soffitWidth,
  eaveReturnOverhang,
  eaveWidth,
  eaveSlope,
  slope,
  roofTopFaces,
  isStart,
  roofTopBSP,
  eaveReturnRoofMaterial,
  offsetZ,
}: MiniHipRoofOptions) {
  slope = Math.max(slope, 0.01);

  const basePlane = planeCopy(railPlane);
  if (!isStart) {
    const reprojectedRail = polyLine3OnPlane(planeProjectPolyLine3(railPlane, rail), railPlane);
    if (reprojectedRail) {
      const [x, y, z] = polyLine3EvalEdge(reprojectedRail, polyLine3EdgeCount(reprojectedRail) - 1, 1, true);
      basePlane.originX = x;
      basePlane.originY = y;
      basePlane.originZ = z;
    }
  }

  const [nx, ny, nz] = planeNormal(railPlane);

  basePlane.vX = -nx;
  basePlane.vY = -ny;
  basePlane.vZ = -nz;

  planeMove(basePlane, 0, 0, offsetZ);

  const peakPlane = planeCopyMoved(basePlane, 0, 0, slope * eaveReturnOverhang);

  const diff = eaveReturnOverhang - soffitWidth;
  const eaveAngle = Math.abs(Math.atan(eaveSlope));
  const eaveOffset = diff < DIST_EPSILON ? fasciaSize / Math.sin(eaveAngle) : 0;

  // eave return rails
  const outerRail = polyLine2ByTwoPoints(eaveOffset, 0, eaveWidth, 0);
  const innerRail = polyLine2New([eaveOffset, -eaveReturnOverhang, eaveWidth, -eaveReturnOverhang]);
  const peakRail = polyLine2New([
    eaveOffset,
    -eaveReturnOverhang,
    Math.max(eaveWidth - eaveReturnOverhang, eaveOffset + 0.1),
    -eaveReturnOverhang,
  ]);

  if (polyLine2Length(outerRail) < DIST_EPSILON && polyLine2Length(innerRail) < DIST_EPSILON) {
    return;
  }

  if (!isStart) {
    polyLine2Move(outerRail, -eaveWidth - eaveOffset, 0);
    polyLine2Move(innerRail, -eaveWidth - eaveOffset, 0);
    polyLine2Move(peakRail, -eaveWidth - eaveOffset + eaveReturnOverhang, 0);
  }
  // preserve rails for later use (measures)
  rails.returnEaveRails.push(
    polyLine3OnPlane(polyLine2ByTwoPoints(outerRail[0], outerRail[1], outerRail[2], outerRail[3]), basePlane),
  );
  rails.returnPeakRails.push(
    polyLine3OnPlane(polyLine2ByTwoPoints(peakRail[0], peakRail[1], peakRail[2], peakRail[3]), peakPlane),
  );

  const ext = extrudeAlongThreeRails(peakRail, outerRail, innerRail, basePlane, peakPlane);

  let faces = isStart ? [ext.endFace, ...ext.leftFaces] : [ext.startFace, ...ext.leftFaces];

  faces = faces.filter((face) => face !== null);

  addEaveReturnRoofTopExtensions({
    basePlane,
    eaveReturnOverhang,
    soffitWidth,
    slope,
    eaveSlope,
    isStart,
    faces: faces as Face[],
  });

  faces = clipFaces({
    faces: faces as Face[],
    options: {
      clippingNodes: [clippingNodeNew(roofTopBSP, true)],
      epsilon: MULTIGABLE_BSP_EPSILON * 100,
    },
  });

  const tag: BuildingGeomTag = { type: "CONDITION", condition: "GABLE_MINI_HIP_ROOF" };

  roofTopFaces.push(
    faceArrayViewEntityNew(
      tagFaces(faces, tag),
      baseDescriptorNew(ViewEntityType.MINI_HIP_ROOF),
      eaveReturnRoofMaterial,
    ),
  );
}

export const DEFAULT_RAFTER_TAIL_EDGE_OFFSET = 20;
export const DEFAULT_RAFTER_TAIL_EXTENSION = 6;

interface FullReturnHipRoofOptions {
  readonly rail: PolyLine3;
  readonly railPlane: Plane;
  readonly rails: RoofRails;
  readonly eaveType: EaveType;
  readonly eaveReturnOverhang: number;
  readonly soffitWidth: number;
  readonly soffitMaterial?: ExteriorMaterial;
  readonly startSlope: number;
  readonly endSlope: number;
  readonly parentSlope: number;
  readonly fasciaBoards: FaceArrayViewEntity[];
  readonly fasciaTopTrims: FaceArrayViewEntity[];
  readonly soffits: GeneratedRoofSoffit[];
  readonly roofTopFaces: FaceArrayViewEntity[];
  readonly addRafterTailExtension?: boolean;
  readonly eaveReturnRoofSlope?: number;
  readonly eaveReturnRoofMaterial?: ExteriorMaterial;
  readonly rafterTailExtensionEdgeOffset?: number;
  readonly rafterTailExtension?: number;
  readonly rafterTailDepth?: number;
  readonly rafterTailClosed?: boolean;
  readonly includeFasciaTopTrim?: boolean;
  readonly fasciaTopTrimSize?: number;
  readonly fasciaSize: number;
  readonly fasciaTopTrimMaterial?: ExteriorMaterial;
  readonly fasciaThickness: number;
  readonly rakeFasciaMaterial?: ExteriorMaterial;
  readonly hasSquareCutEave?: boolean;
}

function gableFullReturnHipRoof({
  rail,
  railPlane,
  rails,
  eaveType,
  eaveReturnOverhang,
  soffitWidth,
  soffitMaterial,
  startSlope,
  endSlope,
  parentSlope,
  fasciaSize,
  addRafterTailExtension = false,
  rafterTailExtensionEdgeOffset = DEFAULT_RAFTER_TAIL_EDGE_OFFSET,
  rafterTailExtension = DEFAULT_RAFTER_TAIL_EXTENSION,
  rafterTailDepth = fasciaSize,
  rafterTailClosed = false,
  includeFasciaTopTrim = false,
  fasciaTopTrimSize = FASCIA_TOP_TRIM_SIZE,
  eaveReturnRoofSlope = parentSlope,
  eaveReturnRoofMaterial,
  fasciaBoards,
  fasciaTopTrims,
  soffits,
  roofTopFaces,
  fasciaTopTrimMaterial,
  fasciaThickness,
  rakeFasciaMaterial,
  hasSquareCutEave,
}: FullReturnHipRoofOptions) {
  const railLength = polyLine3Length(rail);
  const basePlane = planeCopy(railPlane);
  const [nx, ny, nz] = planeNormal(railPlane);

  basePlane.vX = -nx;
  basePlane.vY = -ny;
  basePlane.vZ = -nz;

  const eaveReturnAngle = Math.atan(eaveReturnRoofSlope);
  const [vxNorm, vyNorm, vzNorm] = hasSquareCutEave
    ? planeNormal(planeRotateAroundU(planeCopy(railPlane), -eaveReturnAngle))
    : [0, 0, 1];

  const addMiniHipRoof = (
    peakRail: PolyLine2,
    outerRail: PolyLine2,
    innerRail: PolyLine2,
    basePlane: Plane,
    peakPlane: Plane,
    thickness: number,
    peakElevation: number,
    addCapFaces: boolean = false,
    applySquareCut: boolean = false,
  ) => {
    const faces: Face[] = [];
    if (peakElevation > DIST_EPSILON) {
      const ext = extrudeAlongThreeRails(peakRail, outerRail, innerRail, basePlane, peakPlane);
      faces.push(...ext.leftFaces);
      if (addCapFaces) {
        ext.startFace ? faces.push(ext.startFace) : 0;
        ext.endFace ? faces.push(ext.endFace) : 0;
      }
    } else {
      faces.push(...extrudeAlongTwoRails(peakRail, outerRail, basePlane, peakPlane));
    }
    for (const face of faces) {
      const [dx, dy, dz] = applySquareCut
        ? [thickness * vxNorm, thickness * vyNorm, thickness * vzNorm]
        : [0, 0, thickness];

      const faceToExtrude = applySquareCut
        ? faceOrNullMoveAlongNormal(faceOrNullCopy(face), -thickness - 0.1)
        : faceOrNullMove(faceOrNullCopy(face), 0, 0, -thickness - 0.1);
      if (!!faceToExtrude) {
        const extrusion = extrudeFace(faceToExtrude, dx, dy, dz);
        const brep = extrusionToBRep(extrusion);
        // this doesn't seem to be creating soffit geometry
        // TODO: categorize this correctly
        soffits.push(makeSoffit(faceArrayViewEntityNew(brep.faces, undefined, undefined)));
      }
    }

    roofTopFaces.push(
      faceArrayViewEntityNew(
        tagFaces(faces, { type: "CONDITION", condition: "GABLE_FULL_RETURN_HIP_ROOF" }),
        undefined,
        eaveReturnRoofMaterial,
      ),
    );
  };

  const peakElevation = eaveReturnRoofSlope * eaveReturnOverhang;
  const peakPlane = planeCopyMoved(basePlane, 0, 0, peakElevation);

  const diff = eaveReturnOverhang - soffitWidth;
  const startAngle = Math.abs(Math.atan(startSlope));
  const endAngle = Math.abs(Math.atan(endSlope));
  const startOffset = diff < DIST_EPSILON ? fasciaSize / Math.sin(startAngle) : 0;
  const endOffset = diff < DIST_EPSILON ? fasciaSize / Math.sin(endAngle) : 0;

  // eave return rails
  const outerRail = polyLine2New([startOffset, 0, railLength - endOffset, 0]);
  const innerRail = polyLine2New([startOffset, -eaveReturnOverhang, railLength - endOffset, -eaveReturnOverhang]);
  const peakRail = polyLine2New([
    startOffset + peakElevation / startSlope,
    -eaveReturnOverhang,
    railLength - endOffset - peakElevation / endSlope,
    -eaveReturnOverhang,
  ]);

  if (polyLine2Length(outerRail) < DIST_EPSILON && polyLine2Length(innerRail) < DIST_EPSILON) {
    return;
  }
  // preserve rails for later use (measures)
  rails.returnEaveRails.push(
    polyLine3OnPlane(polyLine2ByTwoPoints(outerRail[0], outerRail[1], outerRail[2], outerRail[3]), basePlane),
  );
  rails.returnPeakRails.push(
    polyLine3OnPlane(polyLine2ByTwoPoints(peakRail[0], peakRail[1], peakRail[2], peakRail[3]), peakPlane),
  );

  const applyRafterTailExtension = addRafterTailExtension && rafterTailExtension > DIST_EPSILON;

  addMiniHipRoof(
    peakRail,
    outerRail,
    innerRail,
    basePlane,
    peakPlane,
    constants.GABLE_ROOF_THICKNESS,
    peakElevation,
    diff > DIST_EPSILON,
    hasSquareCutEave,
  );

  if (includeFasciaTopTrim) {
    const fasciaTopTrimRails: PolyLine3[] = [];
    let extensionRailLeft: PolyLine3 | undefined = undefined;
    let extensionRailRight: PolyLine3 | undefined = undefined;

    const basePlaneOriginial = planeCopy(basePlane);
    const fasciaTrimRail = polyLine3OnPlane(polyLine2ByTwoPoints(railLength, 0, 0, 0), basePlaneOriginial);
    const [startX, startY, startZ] = vec3Add(
      fasciaTrimRail[0],
      fasciaTrimRail[1],
      fasciaTrimRail[2],
      nx * fasciaTopTrimSize,
      ny * fasciaTopTrimSize,
      nz * fasciaTopTrimSize,
    );
    const [endX, endY, endZ] = vec3Add(
      fasciaTrimRail[3],
      fasciaTrimRail[4],
      fasciaTrimRail[5],
      nx * fasciaTopTrimSize,
      ny * fasciaTopTrimSize,
      nz * fasciaTopTrimSize,
    );

    if (applyRafterTailExtension) {
      const startRail = polyLine3OnPlane(
        polyLine2ByTwoPoints(railLength, 0, railLength - rafterTailExtensionEdgeOffset, 0),
        basePlaneOriginial,
      );
      const startRailExtended = polyLine3New([startX, startY, startZ, ...startRail]);

      const extensionRail = polyLine3OnPlane(
        polyLine2ByTwoPoints(railLength - rafterTailExtensionEdgeOffset, 0, rafterTailExtensionEdgeOffset, 0),
        basePlaneOriginial,
      );
      polyLine3Move(
        extensionRail,
        -nx * rafterTailExtension,
        -ny * rafterTailExtension,
        -rafterTailExtension * eaveReturnRoofSlope,
      );

      const endRail = polyLine3OnPlane(
        polyLine2ByTwoPoints(rafterTailExtensionEdgeOffset, 0, 0, 0),
        basePlaneOriginial,
      );

      const endRailExtended = polyLine3New([...endRail, endX, endY, endZ]);

      extensionRailLeft = polyLine3ByTwoPoints(
        startRail[3],
        startRail[4],
        startRail[5],
        extensionRail[0],
        extensionRail[1],
        extensionRail[2],
      );

      extensionRailRight = polyLine3ByTwoPoints(
        extensionRail[3],
        extensionRail[4],
        extensionRail[5],
        endRail[0],
        endRail[1],
        endRail[2],
      );

      polyLine3ExtendBothEnds(extensionRail, FASCIA_TOP_TRIM_THICKNESS, FASCIA_TOP_TRIM_THICKNESS);
      fasciaTopTrimRails.push(startRailExtended);
      fasciaTopTrimRails.push(extensionRail);
      fasciaTopTrimRails.push(endRailExtended);
    } else {
      const fasciaTrimRailExtended = polyLine3New([startX, startY, startZ, ...fasciaTrimRail, endX, endY, endZ]);
      fasciaTopTrimRails.push(fasciaTrimRailExtended);
    }

    const fasciaTrimProfile = squaredFasciaProfile(fasciaTopTrimSize, 0, FASCIA_TOP_TRIM_THICKNESS, 0);
    const fasciaTrimProfileSloped = hasSquareCutEave
      ? squaredSlopeEaveProfile(fasciaTopTrimSize, -eaveReturnRoofSlope, FASCIA_TOP_TRIM_THICKNESS)
      : fasciaTrimProfile;

    let startTangent;
    let endTangent;
    for (const fasciaTopTrimRail of fasciaTopTrimRails) {
      const [startTangentX, startTangentY] = polyLine3EdgeTangent(fasciaTopTrimRail, 0);
      const [endTangentX, endTangentY] = polyLine3EdgeTangent(
        fasciaTopTrimRail,
        polyLine3EdgeCount(fasciaTopTrimRail) - 1,
      );
      startTangent = [...vec2Normalize(startTangentX, startTangentY), 0];
      endTangent = [...vec2Normalize(endTangentX, endTangentY), 0];
      const faciaTrim = extrudeFaceAlongPolyLine3(
        [fasciaTrimProfileSloped],
        fasciaTopTrimRail,
        0,
        0,
        1,
        startTangent,
        endTangent,
      );

      fasciaTopTrims.push(
        faceArrayViewEntityNew(faciaTrim.faces, baseDescriptorNew(ViewEntityType.FASCIA_TRIM), fasciaTopTrimMaterial),
      );
    }
    if (extensionRailLeft && extensionRailRight) {
      for (const fasciaTopTrimRail of [extensionRailLeft, extensionRailRight]) {
        const [startTangentX, startTangentY, startTangentZ] = polyLine3EdgeTangent(fasciaTopTrimRail, 0);
        const [endTangentX, endTangentY, endTangentZ] = polyLine3EdgeTangent(
          fasciaTopTrimRail,
          polyLine3EdgeCount(fasciaTopTrimRail) - 1,
        );
        startTangent = hasSquareCutEave
          ? [...vec3Normalize(startTangentX, startTangentY, startTangentZ)]
          : [...vec2Normalize(startTangentX, startTangentY), 0];
        endTangent = hasSquareCutEave
          ? [...vec3Normalize(endTangentX, endTangentY, endTangentZ)]
          : [...vec2Normalize(endTangentX, endTangentY), 0];
        const faciaTrim = extrudeFaceAlongPolyLine3(
          [fasciaTrimProfile],
          fasciaTopTrimRail,
          0,
          0,
          1,
          startTangent,
          endTangent,
        );

        fasciaTopTrims.push(
          faceArrayViewEntityNew(faciaTrim.faces, baseDescriptorNew(ViewEntityType.FASCIA_TRIM), fasciaTopTrimMaterial),
        );
      }
    }
  }

  if (applyRafterTailExtension) {
    const extensionPeakElevation = rafterTailExtension * eaveReturnRoofSlope;
    const extensionPeakPlane = planeCopyMoved(basePlane, 0, 0, fasciaThickness * eaveReturnRoofSlope);
    const extensionBasePlane = planeCopyMoved(basePlane, 0, 0, -extensionPeakElevation + 10.0 * BSP_EPSILON);

    const extensionOuterRail = polyLine2New([
      rafterTailExtensionEdgeOffset + BSP_EPSILON,
      rafterTailExtension - BSP_EPSILON,
      railLength - rafterTailExtensionEdgeOffset - BSP_EPSILON,
      rafterTailExtension - BSP_EPSILON,
    ]);
    const extensionInnerRail = polyLine2New([
      rafterTailExtensionEdgeOffset + BSP_EPSILON,
      -fasciaThickness,
      railLength - rafterTailExtensionEdgeOffset - BSP_EPSILON,
      -fasciaThickness,
    ]);
    const extensionPeakRail = polyLine2Copy(extensionInnerRail);

    addMiniHipRoof(
      extensionPeakRail,
      extensionOuterRail,
      extensionInnerRail,
      extensionBasePlane,
      extensionPeakPlane,
      constants.GABLE_ROOF_THICKNESS,
      extensionPeakElevation,
      false,
      hasSquareCutEave,
    );

    //add soffit to the extension
    const soffitProfile = hasSquareCutEave
      ? squaredSlopeEaveProfile(
          rafterTailDepth - constants.GABLE_ROOF_THICKNESS,
          eaveReturnRoofSlope,
          (rafterTailExtension + FASCIA_THICKNESS) * Math.sqrt(1 + eaveReturnRoofSlope * eaveReturnRoofSlope),
        )
      : plumbRafterTailExtensionSoffitProfile(
          rafterTailDepth - constants.GABLE_ROOF_THICKNESS,
          eaveReturnRoofSlope,
          rafterTailExtension + FASCIA_THICKNESS,
        );

    // adjust profile to avoid overlapping with the roof top
    const [n2X, n2Y] = hasSquareCutEave ? vec2Normalize(-eaveReturnRoofSlope, 1) : [0, 1];

    polyLine2Move(soffitProfile, -n2X * constants.GABLE_ROOF_THICKNESS, -n2Y * constants.GABLE_ROOF_THICKNESS);

    //add fascia board to the extension
    const fasciaProfile = squaredFasciaProfile(rafterTailDepth, 0, fasciaThickness, 0);
    const fasciaProfileSloped = hasSquareCutEave
      ? squaredSlopeEaveProfile(rafterTailDepth, eaveReturnRoofSlope, fasciaThickness)
      : fasciaProfile;

    const startRail = polyLine3OnPlane(
      polyLine2ByTwoPoints(railLength, 0, railLength - rafterTailExtensionEdgeOffset, 0),
      basePlane,
    );
    const endRail = polyLine3OnPlane(polyLine2ByTwoPoints(rafterTailExtensionEdgeOffset, 0, 0, 0), basePlane);

    const extensionRail = polyLine3OnPlane(
      polyLine2ByTwoPoints(railLength - rafterTailExtensionEdgeOffset, 0, rafterTailExtensionEdgeOffset, 0),
      basePlane,
    );

    polyLine3Move(
      extensionRail,
      -nx * rafterTailExtension,
      -ny * rafterTailExtension,
      -rafterTailExtension * eaveReturnRoofSlope,
    );

    const extensionRailForSoffit = polyLine3Copy(extensionRail);
    polyLine3Move(
      extensionRailForSoffit,
      nx * SOFFIT_FASCIA_BOARD_OVERLAP,
      ny * SOFFIT_FASCIA_BOARD_OVERLAP,
      eaveReturnRoofSlope * SOFFIT_FASCIA_BOARD_OVERLAP,
    );

    const soffitExtrusionRail = polyLine3Reverse(
      polyLine3ExtendBothEnds(extensionRailForSoffit, -FASCIA_THICKNESS, -FASCIA_THICKNESS),
    );

    const { faces } = extrudeFaceAlongPolyLine3([soffitProfile], soffitExtrusionRail, 0, 0, 1);
    const [dirX, dirY, dirZ] = polyLine3EdgeTangent(soffitExtrusionRail, 0);
    addGeneratedSoffit(soffits, eaveType, faces, { dirX, dirY, dirZ }, soffitMaterial, ViewEntityType.EAVE_SOFFIT);

    const extensionRailLeft = polyLine3ByTwoPoints(
      startRail[3],
      startRail[4],
      startRail[5],
      extensionRail[0],
      extensionRail[1],
      extensionRail[2],
    );
    const extensionRailRight = polyLine3ByTwoPoints(
      extensionRail[3],
      extensionRail[4],
      extensionRail[5],
      endRail[0],
      endRail[1],
      endRail[2],
    );

    let startTangent;
    let endTangent;
    {
      const reversedExtensionRail = polyLine3CopyReversed(extensionRail);
      const [startTangentX, startTangentY] = polyLine3EdgeTangent(reversedExtensionRail, 0);
      const [endTangentX, endTangentY] = polyLine3EdgeTangent(
        reversedExtensionRail,
        polyLine3EdgeCount(reversedExtensionRail) - 1,
      );
      startTangent = [...vec2Normalize(startTangentX, startTangentY), 0];
      endTangent = [...vec2Normalize(endTangentX, endTangentY), 0];

      fasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([fasciaProfileSloped], reversedExtensionRail, 0, 0, 1, startTangent, endTangent)
            .faces,
          baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
          rakeFasciaMaterial,
        ),
      );
    }
    {
      const reversedExtensionRailLeft = polyLine3CopyReversed(extensionRailLeft);
      const [startTangentX, startTangentY, startTangentZ] = polyLine3EdgeTangent(reversedExtensionRailLeft, 0);
      const [endTangentX, endTangentY, endTangentZ] = polyLine3EdgeTangent(
        reversedExtensionRailLeft,
        polyLine3EdgeCount(reversedExtensionRailLeft) - 1,
      );
      startTangent = hasSquareCutEave
        ? [...vec3Normalize(startTangentX, startTangentY, startTangentZ)]
        : [...vec2Normalize(startTangentX, startTangentY), 0];
      endTangent = hasSquareCutEave
        ? [...vec3Normalize(endTangentX, endTangentY, endTangentZ)]
        : [...vec2Normalize(endTangentX, endTangentY), 0];
      fasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([fasciaProfile], reversedExtensionRailLeft, 0, 0, 1, startTangent, endTangent)
            .faces,
          baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
          rakeFasciaMaterial,
        ),
      );
    }
    {
      const reversedExtensionRailRight = polyLine3CopyReversed(extensionRailRight);
      const [startTangentX, startTangentY, startTangentZ] = polyLine3EdgeTangent(reversedExtensionRailRight, 0);
      const [endTangentX, endTangentY, endTangentZ] = polyLine3EdgeTangent(
        reversedExtensionRailRight,
        polyLine3EdgeCount(reversedExtensionRailRight) - 1,
      );
      startTangent = hasSquareCutEave
        ? [...vec3Normalize(startTangentX, startTangentY, startTangentZ)]
        : [...vec2Normalize(startTangentX, startTangentY), 0];
      endTangent = hasSquareCutEave
        ? [...vec3Normalize(endTangentX, endTangentY, endTangentZ)]
        : [...vec2Normalize(endTangentX, endTangentY), 0];
      fasciaBoards.push(
        faceArrayViewEntityNew(
          extrudeFaceAlongPolyLine3([fasciaProfile], reversedExtensionRailRight, 0, 0, 1, startTangent, endTangent)
            .faces,
          baseDescriptorNew(ViewEntityType.EAVE_FASCIA),
          rakeFasciaMaterial,
        ),
      );
    }

    if (rafterTailClosed) {
      const soffitInnerRail = polyLine2New([
        rafterTailExtensionEdgeOffset,
        -eaveReturnOverhang,
        railLength - rafterTailExtensionEdgeOffset,
        -eaveReturnOverhang,
      ]);

      const [nX, nY] = hasSquareCutEave ? vec2Normalize(-eaveReturnRoofSlope, 1) : [0, 1];

      const soffitPeakRail = polyLine2Copy(soffitInnerRail);
      const soffitOuterRail = polyLine2Move(polyLine2Copy(extensionOuterRail), 0, rafterTailDepth * nX);

      const soffitPeakPlane = planeCopyMoved(peakPlane, 0, 0, -constants.GABLE_ROOF_THICKNESS);
      const soffitBasePlane = planeCopyMoved(extensionBasePlane, 0, 0, -rafterTailDepth * nY);

      const ext = extrudeAlongThreeRails(
        soffitPeakRail,
        soffitOuterRail,
        soffitInnerRail,
        soffitBasePlane,
        soffitPeakPlane,
      );
      const faces = threeRailExtrusionToFaces(ext);

      const [dirX, dirY, dirZ] = polyLine3EdgeTangent(soffitInnerRail, 0);

      addGeneratedSoffit(soffits, eaveType, faces, { dirX, dirY, dirZ }, soffitMaterial, ViewEntityType.EAVE_SOFFIT);
    }
  }
}

function addFasciaTrim(
  rail: PolyLine3,
  fasciaTopTrimThickness: number,
  fasciaTopTrimSize: number,
  faceArrays: FaceArrayViewEntity[],
  hasSquaredCut: boolean,
  eaveReturnRoofSlope: number,
  fasciaTrimMaterial: ExteriorMaterial | undefined,
) {
  const fasciaBoard = hasSquaredCut
    ? squaredEaveReturnCrossSectionProfile(
        fasciaTopTrimSize,
        -eaveReturnRoofSlope,
        fasciaTopTrimThickness,
        0,
        0,
        0,
        false,
      )
    : plumbEaveReturnCrossSectionProfile(fasciaTopTrimSize, fasciaTopTrimThickness);

  const brep = extrudeFaceAlongPolyLine3([fasciaBoard], rail, 0, 0, 1);

  faceArrays.push(
    faceArrayViewEntityNew(brep.faces, baseDescriptorNew(ViewEntityType.FASCIA_TRIM), fasciaTrimMaterial),
  );
}

function buildEaveReturnClippingPlane(rail: PolyLine3, railPlane: Plane, isEnd: boolean): Plane | null {
  // build the plane for clipping the squared cut eave return on one side
  const [uX, uY, uZ] = planeNormal(railPlane);
  const [nX, nY, nZ] = isEnd
    ? polyLine3EdgeTangent(rail, polyLine3EdgeCount(rail) - 1)
    : polyLine3EdgeTangent(rail, 0, true);
  const [vX, vY, vZ] = vec3Cross(nX, nY, nZ, uX, uY, uZ);

  const [x, y, z] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

  return planeNew(x, y, z, uX, uY, uZ, vX, vY, vZ);
}

function buildEaveReturnClippingBSP(
  rail: PolyLine3,
  railPlane: Plane,
  startEaveCutType: EaveCutType,
  endEaveCutType: EaveCutType,
  addStartDetails: boolean,
  addEndDetails: boolean,
): NodeBSP | null {
  const planes: Plane[] = [];

  // build te plane for the start side (if needed)
  if (addStartDetails && startEaveCutType === EaveCutType.SQUARED) {
    const plane = buildEaveReturnClippingPlane(rail, railPlane, false);

    if (plane) {
      planes.push(plane);
    }
  }

  // build te plane for the end side (if needed)
  if (addEndDetails && endEaveCutType === EaveCutType.SQUARED) {
    const plane = buildEaveReturnClippingPlane(rail, railPlane, true);

    if (plane) {
      planes.push(plane);
    }
  }

  // combine both planes
  return addConvexPlanesToBSPLeaf(planes.map((x) => planeBSPFromPlane(x)));
}

interface ClipEaveReturnEntitiesOptions {
  readonly edge: PolyLine2;
  readonly eaveReturnOverhang: number;
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly rail: PolyLine3;
  readonly railPlane: Plane;
  readonly insideBSP: NodeBSP | null;
  readonly startEaveCutType: EaveCutType;
  readonly endEaveCutType: EaveCutType;
  readonly addStartDetails: boolean;
  readonly addEndDetails: boolean;
  readonly topFaces: FaceArrayViewEntity[];
  readonly fasciaBoards: FaceArrayViewEntity[];
  readonly friezeBoards: FaceArrayViewEntity[];
  readonly soffits: GeneratedRoofSoffit[];
  readonly outFasciaBoards: FaceArrayViewEntity[];
  readonly outFriezeBoards: FaceArrayViewEntity[];
  readonly outSoffits: GeneratedRoofSoffit[];
  readonly outTopFaces: FaceArrayViewEntity[];
}

const tagClipEaveReturnEntities: BuildingGeomTag = {
  type: "OPERATION",
  operation: "CLIP_EAVE_RETURN_ENTITIES",
};

function clipEaveReturnEntities({
  edge,
  eaveReturnOverhang,
  subRoof,
  subRoofs,
  rail,
  railPlane,
  insideBSP,
  startEaveCutType,
  endEaveCutType,
  addStartDetails,
  addEndDetails,
  topFaces,
  fasciaBoards,
  friezeBoards,
  soffits,
  outFasciaBoards,
  outFriezeBoards,
  outSoffits,
  outTopFaces,
}: ClipEaveReturnEntitiesOptions) {
  // build the clipping volume
  const bsp = buildEaveReturnClippingBSP(
    rail,
    railPlane,
    startEaveCutType,
    endEaveCutType,
    addStartDetails,
    addEndDetails,
  );
  const overhangClippingBSPs = collectEaveReturnOverhangClipping({
    edge,
    eaveReturnOverhang,
    subRoof,
    subRoofs,
  });

  const options: ClipFaceContext = {
    clippingNodes: [
      clippingNodeNew(bsp, true),
      clippingNodeNew(insideBSP),
      ...overhangClippingBSPs.map((x) => clippingNodeNew(x)),
    ],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const topOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(insideBSP), ...overhangClippingBSPs.map((x) => clippingNodeNew(x))],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const clipTrimFaces = <T extends FaceArrayViewEntity>(
    faceArrays: T[],
    outFaceArrays: T[],
    options: ClipFaceContext,
  ) => {
    for (const faceArray of faceArrays) {
      const { faces } = faceArray;

      const clippedFaces = clipFaces({
        faces,
        options,
      });

      outFaceArrays.push({ ...faceArray, faces: tagFaces(clippedFaces, tagClipEaveReturnEntities) });
    }
  };

  // clip trim faces
  clipTrimFaces(fasciaBoards, outFasciaBoards, options);
  clipTrimFaces(friezeBoards, outFriezeBoards, options);
  clipTrimFaces(soffits, outSoffits, options);
  clipTrimFaces(topFaces, outTopFaces, topOptions);
}
